import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../config';
import { apiFetch, getAccessToken } from '../utils/auth';
import { getRangiDeviceId } from '../utils/deviceId';
import { formatTaskStatusChangedText, stripEndOfDayDeadlineTime } from '../utils/taskNotifications';
import { isMobileTabViewport } from '../utils/iosViewport';
import { useMobileKeyboardAvoidance } from '../utils/mobileKeyboardAvoidance';
import { useEmployeeId, useMe } from '../context/RoleContext';
import {
    Input,
    Textarea,
    SearchInput,
    Select,
    ActionsMenu,
    ActionsMenuDotsIcon,
    DropdownMenu,
    DROPDOWN_MENU_PORTAL_ATTR,
    EmployeeTaskStatsAvatar,
} from './UI';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';
import actionsMenuStyles from './UI/ActionsMenu.module.css';
import styles from './Tasks.module.css';

const TASK_MENU_OUTSIDE_SELECTOR = [
    styles.detailMenuWrap,
    styles.commentMenuWrap,
    actionsMenuStyles.wrap,
    `[${DROPDOWN_MENU_PORTAL_ATTR}]`,
].join(', ');

const TABS = [
    { key: 'my', label: 'Мои задачи', endpoint: '/tasks/my' },
    { key: 'created', label: 'Поставлены мной', endpoint: '/tasks/created-by-me' },
    { key: 'archive', label: 'Архив', endpoint: null },
];

const FILTER_CHIPS = [
    { key: 'all', label: 'Все' },
    { key: 'important', label: 'Важная' },
    { key: 'today', label: 'Сегодня' },
    { key: 'in_progress', label: 'В работе', status: 'in_progress' },
    { key: 'overdue', label: 'Просроченные' },
];

const STATUS_ORDER = ['new', 'in_progress', 'done', 'accepted', 'archive'];

const STATUS_META = {
    new: { label: 'Новая', cls: styles.statusNew, selectCls: styles.statusSelectNew },
    in_progress: { label: 'В работе', cls: styles.statusProgress, selectCls: styles.statusSelectProgress },
    done: { label: 'Выполнено', cls: styles.statusDone, selectCls: styles.statusSelectDone },
    accepted: { label: 'Принята', cls: styles.statusAccepted, selectCls: styles.statusSelectAccepted },
    archive: { label: 'Архив', cls: styles.statusArchive, selectCls: styles.statusSelectArchive },
};

const REPEAT_OPTIONS = [
    { value: '', label: 'Без повтора' },
    { value: 'daily', label: 'Ежедневно' },
    { value: 'weekly', label: 'Еженедельно' },
    { value: 'monthly', label: 'Ежемесячно' },
];

const REPEAT_FREQUENCY_LABELS = {
    daily: 'Ежедневно',
    weekly: 'Еженедельно',
    monthly: 'Ежемесячно',
};

const TASK_MEDIA_TABS = [
    { key: 'image', label: 'Фото' },
    { key: 'video', label: 'Видео' },
    { key: 'audio', label: 'Аудио' },
    { key: 'file', label: 'Файлы' },
];
const TASK_COMMENTS_PAGE_SIZE = 20;

function buildVisibleCommentsDesc(allAsc, loadedCount) {
    const timeline = Array.isArray(allAsc) ? allAsc : [];
    const count = Math.min(timeline.length, Math.max(0, Number(loadedCount) || 0));
    if (!count) return [];
    return timeline.slice(timeline.length - count).reverse();
}

const TASK_TITLE_MAX_LENGTH = 255;
const TASK_TITLE_TOO_LONG_MESSAGE = `В названии нельзя использовать больше ${TASK_TITLE_MAX_LENGTH} символов`;

function handleTaskTitleChange(value, setTitle, setTitleError) {
    if (value.length > TASK_TITLE_MAX_LENGTH) {
        setTitleError(TASK_TITLE_TOO_LONG_MESSAGE);
        setTitle(value.slice(0, TASK_TITLE_MAX_LENGTH));
        return;
    }
    setTitleError('');
    setTitle(value);
}

function TaskAvatar({
    employeeId,
    thumb,
    avatar,
    alt = '',
    size = 32,
    statsMap,
    className = '',
}) {
    const id = Number(employeeId);
    if (!Number.isFinite(id) || id <= 0) {
        return (
            <EmployeeTaskStatsAvatar
                thumb={thumb}
                avatar={avatar}
                size={size}
                alt={alt}
                className={className}
                fetchStats={false}
            />
        );
    }

    return (
        <EmployeeTaskStatsAvatar
            employeeId={id}
            stats={statsMap?.get(id)}
            fetchStats={false}
            thumb={thumb}
            avatar={avatar}
            size={size}
            alt={alt}
            className={className}
        />
    );
}

function pickTaskEmployee(detail, employeeId) {
    if (!detail || employeeId == null) return null;
    const id = Number(employeeId);
    if (Number(detail.initiator?.id) === id) return detail.initiator;
    if (Number(detail.assignee?.id) === id) return detail.assignee;
    return null;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
}

function isDeadlineUrgent(task) {
    return Boolean(task?.is_overdue || task?.is_deadline_today);
}

function formatDeadline(iso) {
    if (!iso) return 'Без срока';
    try {
        const d = new Date(iso);
        const now = new Date();
        const isSameYear = d.getFullYear() === now.getFullYear();
        const dateOnly = isDateOnlyDeadline(d);
        return d.toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'short',
            ...(isSameYear ? {} : { year: 'numeric' }),
            ...(dateOnly ? {} : { hour: '2-digit', minute: '2-digit' }),
        });
    } catch {
        return '—';
    }
}

function pluralizeTasks(count) {
    const n = Math.abs(Number(count)) || 0;
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 14) return 'задач';
    if (mod10 === 1) return 'задача';
    if (mod10 >= 2 && mod10 <= 4) return 'задачи';
    return 'задач';
}

function formatRelativeTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const dDay = dayStart(d);
        const today = dayStart(now);

        if (dDay === today) {
            return d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        }

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (dDay === dayStart(yesterday)) return 'Вчера';

        return d.toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'short',
        });
    } catch {
        return '';
    }
}

function formatCommentTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const now = new Date();
        const time = d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const dDay = dayStart(d);
        const today = dayStart(now);

        if (dDay === today) return `сегодня ${time}`;

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (dDay === dayStart(yesterday)) return `вчера ${time}`;

        return d.toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
}

function getRepeatFrequencyLabel(frequency) {
    if (!frequency) return 'Повтор';
    return REPEAT_FREQUENCY_LABELS[String(frequency)] || 'Повтор';
}

const ASSIGNEE_PICKER_LABELS = {
    employee: 'Сотрудник',
    department: 'Отдел',
    team: 'Команда',
    side: 'Сторона',
};

const ASSIGNEE_SIDE_OPTIONS = [
    { id: 'light', label: 'Светлые' },
    { id: 'dark', label: 'Тёмные' },
];

const TASK_CREATE_DRAFT_STORAGE_PREFIX = 'tasks_create_draft_v1';

function taskCreateDraftStorageKey(employeeId) {
    const suffix = employeeId != null && employeeId !== '' ? String(employeeId) : 'anon';
    return `${TASK_CREATE_DRAFT_STORAGE_PREFIX}_${suffix}`;
}

function normalizeCreateSubtasksDraft(subtasks) {
    const list = Array.isArray(subtasks) ? subtasks.map((s) => String(s ?? '')) : [''];
    return list.length ? list : [''];
}

function normalizeEditSubtasksDraft(subtasks) {
    const list = Array.isArray(subtasks) ? subtasks : [];
    const mapped = list.map((s) => ({
        id: s?.id != null ? Number(s.id) : null,
        title: String(s?.title ?? ''),
        is_done: !!s?.is_done,
    }));
    return mapped.length ? mapped : [{ id: null, title: '', is_done: false }];
}

function buildEditSubtasksPayload(editSubtasks) {
    const items = [];
    (editSubtasks || []).forEach((s) => {
        const title = String(s?.title || '').trim();
        if (!title) return;
        const item = {
            title,
            is_done: !!s?.is_done,
            position: items.length,
        };
        if (s?.id != null) item.id = Number(s.id);
        items.push(item);
    });
    return items;
}

function normalizeDraftAssignees(list) {
    if (!Array.isArray(list)) return [];
    return list
        .filter((e) => e && e.id != null)
        .map((e) => ({
            id: e.id,
            name: e.name,
            full_name: e.full_name,
            avatar_thumb: e.avatar_thumb,
            avatar: e.avatar,
        }));
}

function isTaskCreateDraftEmpty(draft) {
    if (!draft || typeof draft !== 'object') return true;
    if (String(draft.title || '').trim()) return false;
    if (String(draft.description || '').trim()) return false;
    if (draft.deadlineDate || draft.deadlineTime) return false;
    if (draft.important) return false;
    if (String(draft.repeat || '').trim()) return false;
    if (draft.repeatDate || draft.repeatTime) return false;
    const subtasks = Array.isArray(draft.subtasks) ? draft.subtasks : [];
    if (subtasks.some((s) => String(s || '').trim())) return false;
    if (normalizeDraftAssignees(draft.assignees).length) return false;
    if (Array.isArray(draft.departmentIds) && draft.departmentIds.length) return false;
    if (Array.isArray(draft.teamIds) && draft.teamIds.length) return false;
    if (Array.isArray(draft.sides) && draft.sides.length) return false;
    return true;
}

function readTaskCreateDraft(employeeId) {
    try {
        const raw = localStorage.getItem(taskCreateDraftStorageKey(employeeId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isTaskCreateDraftEmpty(parsed) ? null : parsed;
    } catch {
        return null;
    }
}

function writeTaskCreateDraft(employeeId, draft) {
    try {
        localStorage.setItem(taskCreateDraftStorageKey(employeeId), JSON.stringify(draft));
    } catch { /* ignore quota */ }
}

function clearTaskCreateDraft(employeeId) {
    try {
        localStorage.removeItem(taskCreateDraftStorageKey(employeeId));
    } catch { /* ignore */ }
}

function buildTaskCreateDraftSnapshot({
    title,
    description,
    deadlineDate,
    deadlineTime,
    important,
    repeat,
    repeatDate,
    repeatTime,
    subtasks,
    assignees,
    departmentIds,
    teamIds,
    sides,
}) {
    return {
        title: title ?? '',
        description: description ?? '',
        deadlineDate: deadlineDate ?? '',
        deadlineTime: deadlineTime ?? '',
        important: Boolean(important),
        repeat: repeat ?? '',
        repeatDate: repeatDate ?? '',
        repeatTime: repeatTime ?? '',
        subtasks: normalizeCreateSubtasksDraft(subtasks),
        assignees: normalizeDraftAssignees(assignees),
        departmentIds: Array.isArray(departmentIds) ? departmentIds : [],
        teamIds: Array.isArray(teamIds) ? teamIds : [],
        sides: Array.isArray(sides) ? sides : [],
    };
}

function matchesAssigneeQuery(text, q) {
    if (!q) return false;
    return (text || '').toLowerCase().includes(q);
}

function matchesSideOption(option, q) {
    if (!q) return false;
    const label = option.label.toLowerCase();
    if (matchesAssigneeQuery(label, q)) return true;
    if (q.includes('сторон') || q.includes('сторона')) return true;
    if (option.id === 'light' && (q.includes('свет') || q === 'light')) return true;
    if (option.id === 'dark' && (q.includes('тём') || q.includes('темн') || q === 'dark')) return true;
    return false;
}

const END_OF_DAY_TIME = '23:59';

function isDateOnlyDeadline(date) {
    const h = date.getHours();
    const m = date.getMinutes();
    return (h === 23 && m === 59) || (h === 0 && m === 0);
}

function localDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function buildDeadlineIso(dateStr, timeStr) {
    if (!dateStr) return null;
    const time = timeStr?.trim() || END_OF_DAY_TIME;
    const d = new Date(`${dateStr}T${time}`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

function buildDateTimeIso(dateStr, timeStr) {
    if (!dateStr || !timeStr?.trim()) return null;
    const d = new Date(`${dateStr}T${timeStr.trim()}`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

function countSubtasks(subtasks) {
    const list = subtasks || [];
    return {
        subtasks_total: list.length,
        subtasks_done: list.filter((s) => s.is_done).length,
    };
}

function isSelfAssignedTask(task, employeeId) {
    if (employeeId == null || employeeId === '') return false;
    return Number(task?.assignee_employee_id) === Number(employeeId);
}

function getTaskCounterpartyMeta(task, employeeId) {
    const isAssignee = Number(task?.assignee_employee_id) === Number(employeeId);
    return {
        label: isAssignee ? 'Инициатор' : 'Исполнитель',
        person: isAssignee ? task?.initiator : task?.assignee,
        personId: isAssignee ? task?.initiator_employee_id : task?.assignee_employee_id,
    };
}

function countTasksForTabBadge(tabKey, items) {
    return (items || []).filter((t) => t.status !== 'archive').length;
}

const TABLE_SORT_COLUMNS = ['title', 'deadline', 'person', 'status'];

function compareTasksForTable(a, b, column, direction, isCreatedView) {
    const dir = direction === 'desc' ? -1 : 1;
    let cmp = 0;

    switch (column) {
        case 'title':
            cmp = String(a?.title || '').localeCompare(String(b?.title || ''), 'ru', { sensitivity: 'base' });
            break;
        case 'deadline': {
            const ta = a?.deadline_at ? new Date(a.deadline_at).getTime() : null;
            const tb = b?.deadline_at ? new Date(b.deadline_at).getTime() : null;
            if (ta == null && tb == null) cmp = 0;
            else if (ta == null) cmp = 1;
            else if (tb == null) cmp = -1;
            else cmp = ta - tb;
            break;
        }
        case 'person': {
            const pa = isCreatedView ? a?.assignee : a?.initiator;
            const pb = isCreatedView ? b?.assignee : b?.initiator;
            cmp = String(pa?.name || '').localeCompare(String(pb?.name || ''), 'ru', { sensitivity: 'base' });
            break;
        }
        case 'status': {
            const ia = STATUS_ORDER.indexOf(a?.status);
            const ib = STATUS_ORDER.indexOf(b?.status);
            const ra = ia === -1 ? STATUS_ORDER.length : ia;
            const rb = ib === -1 ? STATUS_ORDER.length : ib;
            cmp = ra - rb;
            break;
        }
        default:
            return 0;
    }

    if (cmp === 0) {
        cmp = Number(a?.id) - Number(b?.id);
    }
    return cmp * dir;
}

function taskToListPatch(task) {
    const counts = countSubtasks(task.subtasks);
    return {
        status: task.status,
        title: task.title,
        description: task.description,
        deadline_at: task.deadline_at,
        is_overdue: task.is_overdue,
        is_deadline_today: task.is_deadline_today,
        is_important: task.is_important,
        updated_at: task.updated_at,
        comments_count: (task.comments || []).length,
        unread_comments_count: getUnreadCommentsCount(task),
        ...counts,
    };
}

function normalizeTaskDetail(task) {
    if (!task) return task;

    const commentAttachmentIds = new Set(
        (task.comments || [])
            .flatMap((comment) => comment?.attachments || [])
            .map((att) => Number(att?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
    );

    const taskAttachments = (task.attachments || []).filter((att) => {
        if (!att) return false;
        const commentId = Number(att.comment_id);
        if (Number.isFinite(commentId) && commentId > 0) return false;
        if (String(att.owner_type || '').toLowerCase() === 'comment') return false;

        const id = Number(att.id);
        if (Number.isFinite(id) && id > 0 && commentAttachmentIds.has(id)) return false;

        return true;
    });

    return {
        ...task,
        attachments: taskAttachments,
    };
}

function getUnreadCommentsCount(task) {
    const count = Number(task?.unread_comments_count);
    return Number.isFinite(count) && count > 0 ? count : 0;
}

async function markTaskCommentsReadRequest(taskId, upToCommentId = null) {
    const r = await apiFetch(`${API_URL}/tasks/${taskId}/comments/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ up_to_comment_id: upToCommentId }),
    });
    if (!r.ok) throw new Error('comments_read_failed');
    return r.json();
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function fetchVapidPublicKey() {
    try {
        const r = await apiFetch(`${API_URL}/chats/webpush/public-key`);
        if (!r.ok) return null;
        const j = await r.json();
        return j?.publicKey || j?.public_key || null;
    } catch {
        return null;
    }
}

async function registerWebPush() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            try {
                await Notification.requestPermission();
            } catch { /* ignore */ }
        }
        const registration = await navigator.serviceWorker.register('/sw.js');
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            const pub = await fetchVapidPublicKey();
            if (!pub) return;
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(pub),
            });
        }
        await apiFetch(`${API_URL}/webpush/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscription }),
        });
    } catch (e) {
        console.warn('WebPush registration failed', e);
    }
}

function isImageAttachment(att) {
    return att?.mime?.startsWith('image/') && att?.url;
}

function AttachmentPreview({ att, taskId, className, onImageClick }) {
    if (isImageAttachment(att)) {
        const handleClick = onImageClick
            || (() => openAttachment(taskId, att.id, att.original_filename));

        return (
            <button
                type="button"
                className={`${styles.attachPreview} ${className || ''}`}
                onClick={handleClick}
                title={att.original_filename}
            >
                <img src={att.url} alt={att.original_filename || 'Вложение'} />
            </button>
        );
    }

    return (
        <button
            type="button"
            className={styles.attachLink}
            onClick={() => openAttachment(taskId, att.id, att.original_filename)}
        >
            📎 {att.original_filename}
        </button>
    );
}

function AttachmentList({ attachments, taskId, onImageOpen, imagePreviewClassName }) {
    const files = [];
    const images = [];
    for (const att of attachments || []) {
        if (isImageAttachment(att)) images.push(att);
        else files.push(att);
    }

    const openImageAt = (idx) => {
        if (!onImageOpen) return;
        onImageOpen(
            images.map((img) => ({
                url: img.url,
                name: img.original_filename || 'Изображение',
            })),
            idx,
        );
    };

    return (
        <>
            {files.map((att) => (
                <AttachmentPreview key={att.id} att={att} taskId={taskId} />
            ))}
            {images.length > 0 && (
                <div className={styles.attachImagesRow}>
                    {images.map((att, idx) => (
                        <AttachmentPreview
                            key={att.id}
                            att={att}
                            taskId={taskId}
                            className={imagePreviewClassName}
                            onImageClick={onImageOpen ? () => openImageAt(idx) : undefined}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

function ImageLightbox({ images, index, onClose, onIndexChange }) {
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const current = images[index];
    const isIOS = useMemo(() => (
        typeof navigator !== 'undefined'
        && (/iPad|iPhone|iPod/.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
    ), []);

    const prev = useCallback(() => {
        onIndexChange(Math.max(0, index - 1));
    }, [index, onIndexChange]);

    const next = useCallback(() => {
        onIndexChange(Math.min(images.length - 1, index + 1));
    }, [images.length, index, onIndexChange]);

    const canGoPrev = index > 0;
    const canGoNext = index < images.length - 1;

    const swipeRef = useRef({
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        dx: 0,
        dy: 0,
    });
    const ignoreCloseClickRef = useRef(false);
    const contentRef = useRef(null);
    const mediaRef = useRef(null);
    const scaleRef = useRef(1);
    const panRef = useRef({ x: 0, y: 0 });
    const touchPanRef = useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
    const mousePanRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
    const pinchRef = useRef({ lastDistance: 0 });
    const LB_MIN_SCALE = 1;
    const LB_MAX_SCALE = 15;
    const LB_PINCH_GAIN = 1.7;

    useEffect(() => { scaleRef.current = scale; }, [scale]);
    useEffect(() => { panRef.current = pan; }, [pan]);
    useEffect(() => {
        setScale(1);
        setPan({ x: 0, y: 0 });
        scaleRef.current = 1;
        panRef.current = { x: 0, y: 0 };
        touchPanRef.current.active = false;
        mousePanRef.current.active = false;
        mousePanRef.current.pointerId = null;
        pinchRef.current.lastDistance = 0;
    }, [index]);

    const getPanBounds = useCallback((scaleValue = scaleRef.current || 1) => {
        const root = contentRef.current;
        const img = mediaRef.current;
        if (!root || !img || scaleValue <= 1) return { x: 0, y: 0 };
        const containerRect = root.getBoundingClientRect();
        const baseW = img.clientWidth || 0;
        const baseH = img.clientHeight || 0;
        if (!baseW || !baseH) return { x: 0, y: 0 };
        const scaledW = baseW * scaleValue;
        const scaledH = baseH * scaleValue;
        return {
            x: Math.max(0, (scaledW - containerRect.width) / 2),
            y: Math.max(0, (scaledH - containerRect.height) / 2),
        };
    }, []);

    const clampPan = useCallback((nextX, nextY, scaleValue = scaleRef.current || 1) => {
        const bounds = getPanBounds(scaleValue);
        return {
            x: Math.max(-bounds.x, Math.min(bounds.x, nextX)),
            y: Math.max(-bounds.y, Math.min(bounds.y, nextY)),
        };
    }, [getPanBounds]);

    const applyScaleAtPoint = useCallback((targetScale, clientX, clientY) => {
        const baseScale = scaleRef.current || 1;
        const basePan = panRef.current || { x: 0, y: 0 };
        const nextScale = Math.max(LB_MIN_SCALE, Math.min(LB_MAX_SCALE, Number(targetScale.toFixed(3))));
        const root = contentRef.current;
        const rect = root?.getBoundingClientRect?.();
        if (!rect || !rect.width || !rect.height || baseScale <= 0 || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
            scaleRef.current = nextScale;
            setScale(nextScale);
            if (nextScale <= 1) {
                panRef.current = { x: 0, y: 0 };
                setPan({ x: 0, y: 0 });
            }
            return;
        }
        let nextPan = basePan;
        if (nextScale <= 1) {
            nextPan = { x: 0, y: 0 };
        } else {
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const ratio = nextScale / baseScale;
            nextPan = clampPan(
                basePan.x + (clientX - centerX - basePan.x) * (1 - ratio),
                basePan.y + (clientY - centerY - basePan.y) * (1 - ratio),
                nextScale,
            );
        }
        scaleRef.current = nextScale;
        panRef.current = nextPan;
        setScale(nextScale);
        setPan(nextPan);
    }, [clampPan]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') prev();
            if (e.key === 'ArrowRight') next();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [next, onClose, prev]);

    useEffect(() => {
        const root = contentRef.current;
        if (!root) return undefined;
        const onTouchMove = (e) => {
            if (!root.contains(e.target)) return;
            if (e.touches.length < 2 && scaleRef.current <= 1) return;
            if (e.cancelable) e.preventDefault();
        };
        document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
        return () => {
            document.removeEventListener('touchmove', onTouchMove, { capture: true });
        };
    }, []);

    useEffect(() => {
        const root = contentRef.current;
        if (!root) return undefined;
        const onWheelDoc = (e) => {
            if (!root.contains(e.target)) return;
            if (e.cancelable) e.preventDefault();
        };
        document.addEventListener('wheel', onWheelDoc, { passive: false, capture: true });
        return () => {
            document.removeEventListener('wheel', onWheelDoc, { capture: true });
        };
    }, []);

    if (!current?.url) return null;

    return (
        <div
            className={styles.imageLightboxOverlay}
            onClick={() => {
                if (ignoreCloseClickRef.current) {
                    ignoreCloseClickRef.current = false;
                    return;
                }
                onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Просмотр изображения"
        >
            <button
                type="button"
                className={styles.imageLightboxClose}
                onClick={onClose}
                aria-label="Закрыть"
            >
                ✕
            </button>

            {canGoPrev && (
                <button
                    type="button"
                    className={`${styles.imageLightboxNav} ${styles.imageLightboxNavLeft}`}
                    onClick={(e) => { e.stopPropagation(); prev(); }}
                    aria-label="Предыдущее"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            )}

            <div
                ref={contentRef}
                className={styles.imageLightboxBody}
                style={{ touchAction: 'none' }}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => {
                    e.preventDefault();
                    const factor = e.deltaY < 0 ? 1.2 : 0.85;
                    applyScaleAtPoint((scaleRef.current || 1) * factor, e.clientX, e.clientY);
                }}
                onPointerDown={(e) => {
                    if (scaleRef.current > 1) return;
                    if (e.pointerType !== 'touch') return;
                    swipeRef.current.active = true;
                    swipeRef.current.pointerId = e.pointerId;
                    swipeRef.current.startX = e.clientX;
                    swipeRef.current.startY = e.clientY;
                    swipeRef.current.dx = 0;
                    swipeRef.current.dy = 0;
                    ignoreCloseClickRef.current = false;
                    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
                }}
                onPointerMove={(e) => {
                    if (scaleRef.current > 1) return;
                    if (!swipeRef.current.active) return;
                    if (swipeRef.current.pointerId !== e.pointerId) return;
                    swipeRef.current.dx = e.clientX - swipeRef.current.startX;
                    swipeRef.current.dy = e.clientY - swipeRef.current.startY;
                }}
                onPointerUp={(e) => {
                    if (scaleRef.current > 1) {
                        swipeRef.current.active = false;
                        swipeRef.current.pointerId = null;
                        return;
                    }
                    if (!swipeRef.current.active) return;
                    if (swipeRef.current.pointerId !== e.pointerId) return;
                    swipeRef.current.active = false;
                    const dx = swipeRef.current.dx;
                    const dy = swipeRef.current.dy;
                    const absX = Math.abs(dx);
                    const absY = Math.abs(dy);
                    if (absX > 50 && absX > absY * 1.2) {
                        ignoreCloseClickRef.current = true;
                        if (dx < 0) next();
                        else prev();
                    }
                }}
                onPointerCancel={() => {
                    swipeRef.current.active = false;
                    swipeRef.current.pointerId = null;
                }}
            >
                <img
                    ref={mediaRef}
                    src={current.url}
                    alt={current.name || 'Изображение'}
                    style={{
                        transform: scale > 1 || pan.x || pan.y
                            ? (isIOS
                                ? `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`
                                : `translate(${pan.x}px, ${pan.y}px) scale(${scale})`)
                            : (isIOS ? 'translate3d(0, 0, 0)' : 'none'),
                        transformOrigin: 'center center',
                        transition: (isIOS || touchPanRef.current.active || pinchRef.current.lastDistance) ? 'none' : 'transform .14s ease',
                        touchAction: scale > 1 ? 'none' : 'pan-y',
                        willChange: isIOS ? 'transform' : undefined,
                        backfaceVisibility: isIOS ? 'hidden' : undefined,
                        WebkitBackfaceVisibility: isIOS ? 'hidden' : undefined,
                    }}
                    onTouchStart={(e) => {
                        if (e.touches.length > 0) {
                            ignoreCloseClickRef.current = false;
                        }
                        if (e.touches.length === 1) {
                            const t = e.touches[0];
                            swipeRef.current.active = true;
                            swipeRef.current.startX = t.clientX;
                            swipeRef.current.startY = t.clientY;
                            swipeRef.current.dx = 0;
                            swipeRef.current.dy = 0;
                            return;
                        }
                        if (e.touches.length !== 2) {
                            pinchRef.current.lastDistance = 0;
                            swipeRef.current.active = false;
                            return;
                        }
                        swipeRef.current.active = false;
                        const [a, b] = e.touches;
                        pinchRef.current.lastDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 0;
                    }}
                    onTouchMove={(e) => {
                        if (e.touches.length === 1 && scaleRef.current <= 1 && images.length > 1) {
                            const t = e.touches[0];
                            swipeRef.current.dx = t.clientX - swipeRef.current.startX;
                            swipeRef.current.dy = t.clientY - swipeRef.current.startY;
                            return;
                        }
                        if (e.touches.length === 1 && scaleRef.current > 1) {
                            const t = e.touches[0];
                            const panState = touchPanRef.current;
                            if (!panState.active) {
                                panState.active = true;
                                panState.startX = t.clientX;
                                panState.startY = t.clientY;
                                panState.startPanX = panRef.current.x;
                                panState.startPanY = panRef.current.y;
                            }
                            const dx = t.clientX - panState.startX;
                            const dy = t.clientY - panState.startY;
                            const clamped = clampPan(panState.startPanX + dx, panState.startPanY + dy, scaleRef.current);
                            panRef.current = clamped;
                            setPan(clamped);
                            ignoreCloseClickRef.current = true;
                            if (e.cancelable) e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                        if (e.touches.length !== 2) return;
                        const [a, b] = e.touches;
                        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
                        const last = pinchRef.current.lastDistance || 0;
                        if (!last || !dist) {
                            pinchRef.current.lastDistance = dist || 0;
                            return;
                        }
                        const rawFactor = dist / last;
                        const factor = 1 + ((rawFactor - 1) * LB_PINCH_GAIN);
                        const focalX = (a.clientX + b.clientX) / 2;
                        const focalY = (a.clientY + b.clientY) / 2;
                        pinchRef.current.lastDistance = dist;
                        if (e.cancelable) e.preventDefault();
                        e.stopPropagation();
                        applyScaleAtPoint((scaleRef.current || 1) * factor, focalX, focalY);
                    }}
                    onTouchEnd={(e) => {
                        if (e.changedTouches.length && scaleRef.current <= 1 && images.length > 1 && swipeRef.current.active) {
                            const t = e.changedTouches[0];
                            const dx = t.clientX - swipeRef.current.startX;
                            const dy = t.clientY - swipeRef.current.startY;
                            const absX = Math.abs(dx);
                            const absY = Math.abs(dy);
                            if (absX >= 40 && absX > absY * 1.3) {
                                ignoreCloseClickRef.current = true;
                                if (dx < 0) next();
                                else prev();
                            }
                        }
                        if (e.touches.length === 0) {
                            swipeRef.current.active = false;
                            touchPanRef.current.active = false;
                        }
                        if (e.touches.length < 2) {
                            pinchRef.current.lastDistance = 0;
                        }
                    }}
                    onClick={(e) => {
                        if (scaleRef.current > 1 || images.length <= 1) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        if (!rect.width) return;
                        const x = e.clientX - rect.left;
                        const ratio = x / rect.width;
                        if (ratio <= 0.33) {
                            e.stopPropagation();
                            prev();
                            return;
                        }
                        if (ratio >= 0.67) {
                            e.stopPropagation();
                            next();
                        }
                    }}
                    onDoubleClick={(e) => {
                        const nextScale = scaleRef.current > 1 ? 1 : 2;
                        applyScaleAtPoint(nextScale, e.clientX, e.clientY);
                    }}
                    onPointerDown={(e) => {
                        if (e.pointerType === 'touch') return;
                        if (scaleRef.current <= 1) return;
                        const st = mousePanRef.current;
                        st.active = true;
                        st.pointerId = e.pointerId;
                        st.startX = e.clientX;
                        st.startY = e.clientY;
                        st.startPanX = panRef.current.x;
                        st.startPanY = panRef.current.y;
                        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onPointerMove={(e) => {
                        const st = mousePanRef.current;
                        if (!st.active || st.pointerId !== e.pointerId) return;
                        const dx = e.clientX - st.startX;
                        const dy = e.clientY - st.startY;
                        const clamped = clampPan(st.startPanX + dx, st.startPanY + dy, scaleRef.current);
                        panRef.current = clamped;
                        setPan(clamped);
                        ignoreCloseClickRef.current = true;
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onPointerUp={(e) => {
                        const st = mousePanRef.current;
                        if (st.pointerId !== e.pointerId) return;
                        st.active = false;
                        st.pointerId = null;
                        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
                    }}
                    onPointerCancel={() => {
                        mousePanRef.current.active = false;
                        mousePanRef.current.pointerId = null;
                    }}
                />
                {current.name && (
                    <div className={styles.imageLightboxCaption}>{current.name}</div>
                )}
            </div>

            {canGoNext && (
                <button
                    type="button"
                    className={`${styles.imageLightboxNav} ${styles.imageLightboxNavRight}`}
                    onClick={(e) => { e.stopPropagation(); next(); }}
                    aria-label="Следующее"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            )}
        </div>
    );
}

function useTaskMediaPage(taskId, kind, enabled, uploaderId = null) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [cursor, setCursor] = useState(null);
    const [error, setError] = useState('');
    const initialLoadDone = useRef(false);

    useEffect(() => {
        setItems([]);
        setLoading(false);
        setHasMore(false);
        setCursor(null);
        setError('');
        initialLoadDone.current = false;
    }, [taskId, kind, uploaderId]);

    const load = useCallback(async (beforeAttachmentId = null) => {
        if (!taskId || !enabled) return;
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.set('kind', kind);
            params.set('limit', '30');
            if (beforeAttachmentId) params.set('before_attachment_id', String(beforeAttachmentId));
            if (uploaderId) params.set('uploader_id', String(uploaderId));
            const r = await apiFetch(`${API_URL}/tasks/${taskId}/media?${params.toString()}`);
            if (!r.ok) throw new Error(String(r.status));
            const data = await r.json();
            const pageItems = Array.isArray(data?.items) ? data.items : [];
            setItems((prev) => (beforeAttachmentId ? [...prev, ...pageItems] : pageItems));
            setHasMore(Boolean(data?.has_more));
            setCursor(data?.next_before_attachment_id ?? null);
        } catch (e) {
            console.error('task media load failed', e);
            setError('Не удалось загрузить медиатеку');
        } finally {
            setLoading(false);
        }
    }, [enabled, kind, taskId, uploaderId]);

    useEffect(() => {
        if (!enabled || initialLoadDone.current) return;
        initialLoadDone.current = true;
        load(null);
    }, [enabled, load]);

    const loadMore = useCallback(() => {
        if (!hasMore || loading || !cursor) return;
        load(cursor);
    }, [cursor, hasMore, load, loading]);

    return { items, loading, hasMore, loadMore, error };
}

function formatMediaGroupDate(iso) {
    if (!iso) return 'Без даты';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Без даты';
    return d.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).toUpperCase();
}

function formatMediaItemDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function humanMediaSize(value) {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) return '';
    if (size >= 1024 * 1024 * 1024) return `${(size / (1024 * 1024 * 1024)).toFixed(1)} ГБ`;
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
    if (size >= 1024) return `${Math.round(size / 1024)} КБ`;
    return `${size} Б`;
}

function mediaFileIcon(item) {
    const mime = String(item?.mime || '').toLowerCase();
    const name = String(item?.original_filename || '').toLowerCase();
    if (mime.includes('zip') || name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z')) return '🗂';
    if (mime.includes('sheet') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv')) return '📊';
    if (mime.includes('presentation') || name.endsWith('.ppt') || name.endsWith('.pptx')) return '📽';
    if (mime.includes('pdf') || name.endsWith('.pdf')) return '📄';
    return '📝';
}

const SYSTEM_TASK_TYPES = new Set([
    'task_created',
    'task_updated',
    'task_status_changed',
    'task_overdue',
    'task_deadline_today',
    'task_repeat_updated',
]);

const HIDDEN_TASK_NOTIFICATION_TYPES = new Set(
    ['task_deadline_today', 'task_overdue'].filter((t) => SYSTEM_TASK_TYPES.has(t)),
);

function shouldHideTaskNotificationFromFeed(notif) {
    const type = String(notif?.type || '').toLowerCase();
    return HIDDEN_TASK_NOTIFICATION_TYPES.has(type);
}

function filterTaskNotificationsForFeed(items) {
    return (Array.isArray(items) ? items : []).filter((n) => !shouldHideTaskNotificationFromFeed(n));
}

function normalizePastedFileName(file) {
    const name = String(file?.name || '').trim();
    const isGeneric = !name || /^(image|blob|file)(\.\w+)?$/i.test(name);
    if (!isGeneric) return file;
    const ext = (file.type || 'application/octet-stream').split('/')[1]?.split(';')[0] || 'bin';
    return new File([file], `screenshot-${Date.now()}.${ext}`, {
        type: file.type,
        lastModified: file.lastModified,
    });
}

function filesFromClipboardEvent(e) {
    return Array.from(e.clipboardData?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => normalizePastedFileName(item.getAsFile()))
        .filter(Boolean);
}

function formatTaskNotificationEvent(notif) {
    const type = notif?.type || '';
    const actorShort = (notif.actor?.name || '').trim().split(/\s+/)[0] || '';
    const body = stripEndOfDayDeadlineTime((notif.body || '').trim());

    switch (type) {
        case 'task_created':
            return actorShort ? `${actorShort} поставил(а) задачу` : 'Поставлена новая задача';
        case 'task_updated':
            return actorShort ? `${actorShort} обновил(а) задачу` : 'Задача обновлена';
        case 'task_status_changed':
            return formatTaskStatusChangedText({ actorName: actorShort, body });
        case 'task_comment':
            if (body) {
                return actorShort
                    ? `${actorShort} оставил(а) комментарий: ${body}`
                    : body;
            }
            return actorShort ? `${actorShort} оставил(а) комментарий` : 'Новый комментарий';
        case 'task_overdue':
            return body || 'Задача просрочена';
        case 'task_repeat_updated':
            return actorShort ? `${actorShort} изменил(а) повторение` : 'Обновлено повторение';
        default:
            if (body) return body;
            return (notif?.title || 'Новое событие').trim() || 'Новое событие';
    }
}

function TaskBellIcon() {
    return (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function TaskNotificationsPanel({
    isOpen,
    onClose,
    items,
    loading,
    hasMore,
    onLoadMore,
    onOpenItem,
    onMarkAllRead,
    markAllBusy,
}) {
    const panelRef = useRef(null);
    const loaderRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!loaderRef.current || !hasMore || loading) return undefined;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) onLoadMore?.();
        }, { threshold: 0.1 });
        observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading, onLoadMore]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`${styles.notifPanelBackdrop} ${styles.notifPanelBackdropVisible}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                ref={panelRef}
                className={`${styles.notifPanel} ${styles.notifPanelOpen}`}
                onClick={(e) => e.stopPropagation()}
                aria-label="Лента событий по задачам"
            >
                <div className={styles.notifPanelHeader}>
                    <button type="button" className={styles.notifPanelBackBtn} onClick={onClose} aria-label="Закрыть ленту">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <div className={styles.notifPanelTitleWrap}>
                        <div className={styles.notifPanelTitle}>Лента событий</div>
                        <div className={styles.notifPanelSubtitle}>Уведомления по задачам</div>
                    </div>
                    {items.some((n) => !n.is_read) && (
                        <button
                            type="button"
                            className={styles.notifMarkAllBtn}
                            onClick={onMarkAllRead}
                            disabled={markAllBusy}
                        >
                            Прочитать все
                        </button>
                    )}
                </div>
                <div className={styles.notifPanelBody}>
                    {loading && items.length === 0 && (
                        <div className={styles.notifState}>Загрузка…</div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className={styles.notifState}>Событий пока нет</div>
                    )}
                    <ul className={styles.notifList}>
                        {items.map((item) => {
                            const taskLabel = item.task_title || `Задача #${item.task_id}`;
                            const eventText = formatTaskNotificationEvent(item);
                            return (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className={`${styles.notifItem} ${!item.is_read ? styles.notifItemUnread : ''}`}
                                        onClick={() => onOpenItem?.(item)}
                                    >
                                        <div className={styles.notifItemMain}>
                                            <div className={styles.notifItemTaskName}>{taskLabel}</div>
                                            <div className={styles.notifItemEvent}>{eventText}</div>
                                        </div>
                                        <div className={styles.notifItemTime}>{formatDateTime(item.created_at)}</div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {hasMore && <div ref={loaderRef} className={styles.notifLoader} aria-hidden="true" />}
                </div>
            </aside>
        </>
    );
}

function TaskMediaPanel({
    isOpen,
    onClose,
    task,
    onOpenImageLightbox,
}) {
    const [activeTab, setActiveTab] = useState('image');
    const { items, loading, hasMore, loadMore, error } = useTaskMediaPage(task?.id, activeTab, isOpen);
    const panelRef = useRef(null);
    const loaderRef = useRef(null);
    const activeTabIndex = useMemo(
        () => Math.max(0, TASK_MEDIA_TABS.findIndex((tab) => tab.key === activeTab)),
        [activeTab]
    );
    const fileGroups = useMemo(() => {
        if (activeTab !== 'file') return [];
        const groups = [];
        let lastLabel = '';
        for (const item of items) {
            const label = formatMediaGroupDate(item?.created_at);
            if (!groups.length || lastLabel !== label) {
                groups.push({ label, items: [] });
                lastLabel = label;
            }
            groups[groups.length - 1].items.push(item);
        }
        return groups;
    }, [activeTab, items]);

    useEffect(() => {
        setActiveTab('image');
    }, [task?.id]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!loaderRef.current || !hasMore) return undefined;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) loadMore();
        }, { threshold: 0.1 });
        observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [hasMore, loadMore]);

    if (!task) return null;

    const imageItems = items.filter((item) => item?.preview_url);

    return (
        <>
            <div
                className={`${styles.mediaPanelBackdrop} ${isOpen ? styles.mediaPanelBackdropVisible : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    const rect = panelRef.current?.getBoundingClientRect?.();
                    if (rect) {
                        const { clientX: x, clientY: y } = e;
                        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return;
                    }
                    onClose?.();
                }}
                aria-hidden="true"
            />
            <aside
                ref={panelRef}
                className={`${styles.mediaPanel} ${isOpen ? styles.mediaPanelOpen : ''}`}
                onClick={(e) => e.stopPropagation()}
                aria-label="Медиатека задачи"
            >
                <div className={styles.mediaPanelHeader}>
                    <button type="button" className={styles.mediaPanelBackBtn} onClick={onClose} aria-label="Закрыть медиатеку">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                    <div className={styles.mediaPanelTitleWrap}>
                        <div className={styles.mediaPanelTitle}>Медиатека</div>
                        <div className={styles.mediaPanelSubtitle}>{task.title || `Задача #${task.id}`}</div>
                    </div>
                </div>

                <div className={styles.mediaTabs} role="tablist">
                    {TASK_MEDIA_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`${styles.mediaTab} ${activeTab === tab.key ? styles.mediaTabActive : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                    <div
                        className={styles.mediaTabsIndicator}
                        aria-hidden="true"
                        style={{
                            width: `${100 / TASK_MEDIA_TABS.length}%`,
                            transform: `translateX(${activeTabIndex * 100}%)`,
                        }}
                    />
                </div>

                <div className={styles.mediaPanelBody}>
                    {error && <div className={styles.mediaState}>{error}</div>}
                    {!error && !loading && items.length === 0 && (
                        <div className={styles.mediaState}>Пока нет файлов этого типа</div>
                    )}

                    {!error && activeTab === 'image' && imageItems.length > 0 && (
                        <div className={styles.mediaGrid}>
                            {imageItems.map((item, index) => (
                                <button
                                    key={item.attachment_id}
                                    type="button"
                                    className={styles.mediaGridItem}
                                    onClick={() => {
                                        onOpenImageLightbox?.(
                                            imageItems.map((it) => ({
                                                url: it.preview_url,
                                                name: it.original_filename || 'Изображение',
                                            })),
                                            index
                                        );
                                    }}
                                    title={item.original_filename || ''}
                                >
                                    <img src={item.preview_url} alt={item.original_filename || ''} loading="lazy" />
                                </button>
                            ))}
                        </div>
                    )}

                    {!error && activeTab === 'file' && items.length > 0 && (
                        <div className={styles.mediaList}>
                            {fileGroups.map((group) => (
                                <div key={group.label} className={styles.mediaDateGroup}>
                                    <div className={styles.mediaDateLabel}>{group.label}</div>
                                    {group.items.map((item) => (
                                        <div key={item.attachment_id} className={styles.mediaFileRow}>
                                            <button
                                                type="button"
                                                className={styles.mediaFileRowMain}
                                                onClick={() => openAttachment(task.id, item.attachment_id, item.original_filename)}
                                                title={item.original_filename || `Файл #${item.attachment_id}`}
                                            >
                                                <span className={styles.mediaFileRowIcon} aria-hidden="true">{mediaFileIcon(item)}</span>
                                                <span className={styles.mediaFileRowMeta}>
                                                    <span className={styles.mediaFileRowName}>{item.original_filename || `Файл #${item.attachment_id}`}</span>
                                                    <span className={styles.mediaFileRowSub}>
                                                        {item?.uploaded_by?.name || '—'}
                                                        {' · '}
                                                        {formatMediaItemDate(item.created_at)}
                                                        {item?.size ? ` · ${humanMediaSize(item.size)}` : ''}
                                                    </span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.mediaFileRowDl}
                                                onClick={() => openAttachment(task.id, item.attachment_id, item.original_filename)}
                                                aria-label={`Скачать ${item.original_filename || `файл ${item.attachment_id}`}`}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {!error && activeTab !== 'image' && activeTab !== 'file' && items.length > 0 && (
                        <div className={styles.mediaList}>
                            {items.map((item) => (
                                <div key={item.attachment_id} className={styles.mediaListItem}>
                                    <div className={styles.mediaListMeta}>
                                        <div className={styles.mediaListName}>{item.original_filename || `Файл #${item.attachment_id}`}</div>
                                        <div className={styles.mediaListSub}>
                                            <span>{formatDateTime(item.created_at)}</span>
                                            {item?.uploaded_by?.name && (
                                                <>
                                                    <span className={styles.mediaListDot}>·</span>
                                                    <span>{item.uploaded_by.name}</span>
                                                </>
                                            )}
                                            {item?.size ? (
                                                <>
                                                    <span className={styles.mediaListDot}>·</span>
                                                    <span>{Math.max(1, Math.round(item.size / 1024))} КБ</span>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.mediaDownloadBtn}
                                        onClick={() => openAttachment(task.id, item.attachment_id, item.original_filename)}
                                    >
                                        Скачать
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasMore && (
                        <div ref={loaderRef} className={styles.mediaLoadMore}>
                            {loading && <span className={styles.mediaSpinner} />}
                        </div>
                    )}
                    {loading && items.length === 0 && (
                        <div className={styles.mediaState}>
                            <span className={styles.mediaSpinner} />
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || { label: status, cls: styles.statusNew };
    return <span className={`${styles.statusBadge} ${meta.cls}`}>{meta.label}</span>;
}

const ASSIGNEE_STATUS_OPTIONS = ['new', 'in_progress', 'done'];
const INITIATOR_STATUS_OPTIONS = ['in_progress', 'accepted', 'archive'];

function getAllowedStatuses(task, employeeId) {
    const isAssignee = Number(task?.assignee_employee_id) === Number(employeeId);
    const isInitiator = Number(task?.initiator_employee_id) === Number(employeeId);
    if (isAssignee) return ASSIGNEE_STATUS_OPTIONS;
    if (isInitiator) return INITIATOR_STATUS_OPTIONS;
    return [];
}

function getStatusSelectOptions(allowedStatuses) {
    return STATUS_ORDER.filter((s) => allowedStatuses.includes(s));
}

function getStatusSelectOptionsForDetail(task, employeeId) {
    const current = task?.status;
    const isInitiator = Number(task?.initiator_employee_id) === Number(employeeId);
    const isAssignee = Number(task?.assignee_employee_id) === Number(employeeId);
    const isSelfTask = isInitiator && isAssignee;

    if (isSelfTask) {
        if (current === 'archive') return ['archive'];
        if (current === 'done') return ['new', 'in_progress', 'done', 'archive'];
        if (current === 'accepted') return ['new', 'in_progress', 'done', 'archive'];
        return ['new', 'in_progress', 'done'];
    }

    if (current === 'archive') {
        return ['archive'];
    }

    if (current === 'accepted') {
        if (isInitiator) return ['in_progress', 'accepted', 'archive'];
        if (isAssignee) return ['accepted', 'archive'];
        return ['accepted'];
    }

    const options = getStatusSelectOptions(getAllowedStatuses(task, employeeId))
        .filter((s) => s !== 'archive' || current === 'accepted');
    if (current && !options.includes(current)) {
        return STATUS_ORDER.filter((s) => options.includes(s) || s === current);
    }
    return options;
}

function pickStatusOnlyPatch(task, fallbackStatus) {
    const normalized = normalizeTaskDetail(task || {});
    const patch = {
        status: normalized?.status || fallbackStatus,
    };
    if (Object.prototype.hasOwnProperty.call(normalized, 'is_overdue')) {
        patch.is_overdue = Boolean(normalized.is_overdue);
    }
    if (Object.prototype.hasOwnProperty.call(normalized, 'is_deadline_today')) {
        patch.is_deadline_today = Boolean(normalized.is_deadline_today);
    }
    return patch;
}

async function openAttachment(taskId, attId, filename = '') {
    try {
        const r = await apiFetch(`${API_URL}/tasks/${taskId}/attachments/${attId}/download`);
        if (!r.ok) return;
        const data = await r.json();
        if (!data?.url) return;
        const a = document.createElement('a');
        a.href = data.url;
        a.target = '_blank';
        a.rel = 'noreferrer';
        if (filename) a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    } catch { /* ignore */ }
}

async function compressImageFile(file, opts = {}) {
    const {
        maxSide = 1920,
        quality = 0.82,
    } = opts;

    try {
        const type = (file?.type || '').toLowerCase();
        if (!type.startsWith('image/')) return file;

        let bitmap = null;
        if (window.createImageBitmap) {
            try {
                bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
            } catch {
                bitmap = await createImageBitmap(file);
            }
        }

        let srcW = bitmap ? bitmap.width : 0;
        let srcH = bitmap ? bitmap.height : 0;

        if (!bitmap || !srcW || !srcH) {
            const url = URL.createObjectURL(file);
            const img = await new Promise((resolve, reject) => {
                const el = new Image();
                el.onload = () => resolve(el);
                el.onerror = (e) => reject(e);
                el.src = url;
            });
            try { URL.revokeObjectURL(url); } catch { /* ignore */ }
            srcW = img.naturalWidth || img.width;
            srcH = img.naturalHeight || img.height;

            const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
            const dstW = Math.max(1, Math.round(srcW * scale));
            const dstH = Math.max(1, Math.round(srcH * scale));

            const canvas = document.createElement('canvas');
            canvas.width = dstW;
            canvas.height = dstH;
            const ctx = canvas.getContext('2d', { alpha: false });
            ctx.drawImage(img, 0, 0, dstW, dstH);

            const blob = await new Promise((resolve) => {
                canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
            });
            if (!blob) return file;

            const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
            const newName = `${baseName}.jpg`;
            return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
        }

        const scale = Math.min(1, maxSide / Math.max(srcW, srcH));
        const dstW = Math.max(1, Math.round(srcW * scale));
        const dstH = Math.max(1, Math.round(srcH * scale));

        const canvas = document.createElement('canvas');
        canvas.width = dstW;
        canvas.height = dstH;
        const ctx = canvas.getContext('2d', { alpha: false });
        ctx.drawImage(bitmap, 0, 0, dstW, dstH);

        const blob = await new Promise((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
        });
        try { bitmap.close?.(); } catch { /* ignore */ }
        if (!blob) return file;

        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
        const newName = `${baseName}.jpg`;
        return new File([blob], newName, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
    } catch {
        return file;
    }
}

async function uploadFilesToTask(taskId, files, attachTo = 'task') {
    if (!files?.length) return [];
    const processedFiles = [];
    for (const file of files) {
        const type = (file?.type || '').toLowerCase();
        const processedFile = type.startsWith('image/') ? await compressImageFile(file) : file;
        processedFiles.push(processedFile);
    }

    const presignRes = await apiFetch(`${API_URL}/tasks/${taskId}/attachments/presign`, {
        method: 'POST',
        body: JSON.stringify({
            attach_to: attachTo,
            files: processedFiles.map((f) => ({
                filename: f.name,
                mime: f.type || 'application/octet-stream',
                size: f.size,
            })),
        }),
    });
    if (!presignRes.ok) throw new Error('presign_failed');
    const items = await presignRes.json();
    const attachments = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const file = processedFiles[i];
        const putRes = await fetch(item.url, {
            method: item.method || 'PUT',
            headers: item.headers || { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
        });
        if (!putRes.ok) throw new Error('upload_failed');
        attachments.push({
            s3_key: item.s3_key,
            original_filename: item.original_filename || file.name,
            mime: item.mime || file.type || 'application/octet-stream',
            size: item.size ?? file.size,
        });
    }
    return attachments;
}

export default function Tasks() {
    const employeeId = useEmployeeId();
    const me = useMe();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tab, setTab] = useState('my');
    const [listMode, setListMode] = useState('my');
    const [chip, setChip] = useState('all');
    const [tableSort, setTableSort] = useState({ column: null, direction: 'asc' });
    const [filterPersonId, setFilterPersonId] = useState(null);
    const [filterPersonName, setFilterPersonName] = useState('');
    const [personFilterOpen, setPersonFilterOpen] = useState(false);
    const [personFilterQuery, setPersonFilterQuery] = useState('');
    const [personFilterResults, setPersonFilterResults] = useState([]);
    const personFilterAnchorRef = useRef(null);
    const [searchQ, setSearchQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);

    const [detailId, setDetailId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [createBusy, setCreateBusy] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createTitleError, setCreateTitleError] = useState('');
    const [createDesc, setCreateDesc] = useState('');
    const [createDeadlineDate, setCreateDeadlineDate] = useState('');
    const [createDeadlineTime, setCreateDeadlineTime] = useState('');
    const [createImportant, setCreateImportant] = useState(false);
    const [createRepeat, setCreateRepeat] = useState('');
    const [createRepeatDate, setCreateRepeatDate] = useState('');
    const [createRepeatTime, setCreateRepeatTime] = useState('');
    const [createSubtasks, setCreateSubtasks] = useState(['']);
    const [createFiles, setCreateFiles] = useState([]);
    const [createDragActive, setCreateDragActive] = useState(false);
    const createDragCounterRef = useRef(0);
    const createFileInputRef = useRef(null);
    const detailModalRef = useRef(null);
    const createModalRef = useRef(null);
    const editModalRef = useRef(null);
    const detailComposerRef = useRef(null);
    const [assigneeQuery, setAssigneeQuery] = useState('');
    const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedDeptIds, setSelectedDeptIds] = useState([]);
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [selectedSides, setSelectedSides] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [commentBusy, setCommentBusy] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentBusy, setEditingCommentBusy] = useState(false);
    const commentInputRef = useRef(null);
    const [deletingCommentId, setDeletingCommentId] = useState(null);
    const [commentMenuOpenId, setCommentMenuOpenId] = useState(null);
    const commentMenuAnchorRef = useRef(null);
    const [detailDragActive, setDetailDragActive] = useState(false);
    const detailScrollRef = useRef(null);
    const detailMobileScrollRef = useRef(null);
    const detailDragCounterRef = useRef(0);

    const getDetailScrollEl = useCallback(() => {
        if (isMobileTabViewport()) {
            return detailMobileScrollRef.current || detailScrollRef.current;
        }
        return detailScrollRef.current;
    }, []);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsHasMore, setCommentsHasMore] = useState(false);
    const commentsBeforeIdRef = useRef(null);
    const commentsLoadingRef = useRef(false);
    const commentsTaskIdRef = useRef(null);
    const commentsPagingArmedRef = useRef(false);
    const commentsTimelineRef = useRef([]);
    const commentsLoadedCountRef = useRef(0);
    const commentsAppendingPageRef = useRef(false);
    const [statusBusy, setStatusBusy] = useState(false);
    const [detailMenuOpen, setDetailMenuOpen] = useState(false);
    const [isMobileDetailView, setIsMobileDetailView] = useState(() => isMobileTabViewport());
    const [imageLightbox, setImageLightbox] = useState(null);
    const [mediaPanelOpen, setMediaPanelOpen] = useState(false);
    const [notifPanelOpen, setNotifPanelOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifHasMore, setNotifHasMore] = useState(false);
    const [notifBeforeId, setNotifBeforeId] = useState(null);
    const [unreadNotifCount, setUnreadNotifCount] = useState(0);
    const [markAllNotifBusy, setMarkAllNotifBusy] = useState(false);
    const notifPanelOpenRef = useRef(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editBusy, setEditBusy] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editTitleError, setEditTitleError] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editDeadlineDate, setEditDeadlineDate] = useState('');
    const [editDeadlineTime, setEditDeadlineTime] = useState('');
    const [editImportant, setEditImportant] = useState(false);
    const [editSubtasks, setEditSubtasks] = useState([]);

    const [tabCounts, setTabCounts] = useState({ my: 0, created: 0, archive: 0 });
    const [repeatingItems, setRepeatingItems] = useState([]);
    const [repeatBusyId, setRepeatBusyId] = useState(null);
    const [showAllMobileRepeats, setShowAllMobileRepeats] = useState(false);
    const tabButtonRefs = useRef({});
    const tabsBarRef = useRef(null);
    const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0 });
    const skipDetailRefreshRef = useRef({ taskId: null, until: 0 });
    const shouldStickDetailTopRef = useRef(true);
    const prevDetailCommentsCountRef = useRef(0);
    const prevDetailIdRef = useRef(null);
    const forceScrollToTopOnNextCommentRef = useRef(false);
    const tabsDragStateRef = useRef({
        active: false,
        startX: 0,
        startScrollLeft: 0,
        moved: false,
    });
    const tabsSuppressClickRef = useRef(false);

    const updateTabIndicator = useCallback(() => {
        const activeNode = tabButtonRefs.current[tab];
        if (!activeNode) return;
        setTabIndicator({
            left: activeNode.offsetLeft,
            width: activeNode.offsetWidth,
        });
    }, [tab]);

    useLayoutEffect(() => {
        updateTabIndicator();
    }, [tab, tabCounts, updateTabIndicator]);

    useEffect(() => {
        window.addEventListener('resize', updateTabIndicator);
        return () => window.removeEventListener('resize', updateTabIndicator);
    }, [updateTabIndicator]);

    const handleTabsMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        const node = tabsBarRef.current;
        if (!node) return;
        tabsDragStateRef.current = {
            active: true,
            startX: e.clientX,
            startScrollLeft: node.scrollLeft,
            moved: false,
        };
        tabsSuppressClickRef.current = false;

        const onMove = (moveEvent) => {
            const state = tabsDragStateRef.current;
            if (!state.active) return;
            const delta = moveEvent.clientX - state.startX;
            if (Math.abs(delta) > 4) {
                state.moved = true;
                tabsSuppressClickRef.current = true;
                node.scrollLeft = state.startScrollLeft - delta;
            }
        };
        const onUp = () => {
            tabsDragStateRef.current.active = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.setTimeout(() => {
                tabsSuppressClickRef.current = false;
            }, 0);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 300);
        return () => clearTimeout(t);
    }, [searchQ]);

    useEffect(() => {
        setTableSort({ column: null, direction: 'asc' });
    }, [tab, chip, debouncedQ, filterPersonId]);

    useEffect(() => {
        registerWebPush();
    }, []);

    useEffect(() => {
        document.body.classList.add('tasks-page-open');
        return () => document.body.classList.remove('tasks-page-open');
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 767px)');
        const sync = () => setIsMobileDetailView(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        setDetailMenuOpen(false);
    }, [isMobileDetailView]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!detailMenuOpen && !commentMenuOpenId) return;
            if (!e.target.closest?.(TASK_MENU_OUTSIDE_SELECTOR)) {
                setDetailMenuOpen(false);
                setCommentMenuOpenId(null);
            }
        };
        document.addEventListener('click', onDocClick, true);
        return () => document.removeEventListener('click', onDocClick, true);
    }, [detailMenuOpen, commentMenuOpenId]);

    const listEndpoint = useMemo(() => {
        const mode = tab === 'archive' ? listMode : tab;
        return mode === 'created' ? '/tasks/created-by-me' : '/tasks/my';
    }, [tab, listMode]);

    const isCreatedView = tab === 'created' || (tab === 'archive' && listMode === 'created');
    const personFilterLabel = isCreatedView ? 'Исполнитель' : 'Инициатор';

    const taskEmployeeIds = useMemo(() => {
        const ids = new Set();
        const push = (value) => {
            const id = Number(value);
            if (Number.isFinite(id) && id > 0) ids.add(id);
        };

        for (const task of items) {
            const person = isCreatedView ? task?.assignee : task?.initiator;
            push(person?.id);
            push(task?.assignee_employee_id);
            push(task?.initiator_employee_id);
        }

        for (const emp of personFilterResults) push(emp.id);
        for (const emp of selectedAssignees) push(emp.id);
        for (const emp of employeeSearchResults) push(emp.id);

        for (const comment of detail?.comments || []) {
            push(comment?.author?.id);
            push(comment?.author_id);
        }

        if (detail) {
            push(detail.assignee?.id);
            push(detail.initiator?.id);
            push(detail.assignee_employee_id);
            push(detail.initiator_employee_id);
        }

        return [...ids];
    }, [
        items,
        isCreatedView,
        personFilterResults,
        selectedAssignees,
        employeeSearchResults,
        detail,
    ]);

    const employeeTaskStatsMap = useEmployeeTaskStats(taskEmployeeIds);

    const fetchTabCounts = useCallback(async () => {
        try {
            const [myR, createdR, archMyR, archCreatedR] = await Promise.all([
                apiFetch(`${API_URL}/tasks/my?limit=100&offset=0`),
                apiFetch(`${API_URL}/tasks/created-by-me?limit=100&offset=0`),
                apiFetch(`${API_URL}/tasks/my?limit=100&offset=0&status=archive`),
                apiFetch(`${API_URL}/tasks/created-by-me?limit=100&offset=0&status=archive`),
            ]);

            const parseItems = async (res) => {
                if (!res?.ok) return [];
                const data = await res.json();
                return Array.isArray(data?.items) ? data.items : [];
            };

            const [myItems, createdItems, archMy, archCreated] = await Promise.all([
                parseItems(myR),
                parseItems(createdR),
                parseItems(archMyR),
                parseItems(archCreatedR),
            ]);

            setTabCounts({
                my: countTasksForTabBadge('my', myItems),
                created: countTasksForTabBadge(
                    'created',
                    createdItems.filter((t) => !isSelfAssignedTask(t, employeeId))
                ),
                archive: archMy.length + archCreated.length,
            });
        } catch {
            /* ignore */
        }
    }, [employeeId]);

    const loadRepeatingTasks = useCallback(async () => {
        try {
            const r = await apiFetch(`${API_URL}/tasks/repeats/active`);
            if (!r.ok) throw new Error('repeat_list_failed');
            const rules = await r.json();
            const activeRules = (Array.isArray(rules) ? rules : []).filter((rule) => rule?.is_active);
            if (!activeRules.length) {
                setRepeatingItems([]);
                return;
            }

            const items = await Promise.all(
                activeRules.map(async (rule) => {
                    const sourceTaskId = Number(rule.source_task_id);
                    try {
                        const taskResponse = await apiFetch(`${API_URL}/tasks/${sourceTaskId}`);
                        if (taskResponse.ok) {
                            const task = await taskResponse.json();
                            return { ...task, id: sourceTaskId, repeat_rule: rule };
                        }
                    } catch {
                        /* fallback below */
                    }
                    return {
                        id: sourceTaskId,
                        title: `Задача #${sourceTaskId}`,
                        initiator_employee_id: employeeId,
                        repeat_rule: rule,
                    };
                })
            );

            setRepeatingItems(items);
        } catch {
            setRepeatingItems([]);
        }
    }, [employeeId]);

    const loadList = useCallback(async (reset = true, forcedOffset = null) => {
        setError('');
        const off = forcedOffset != null ? forcedOffset : (reset ? 0 : offset);
        if (reset) setLoading(true);

        try {
            const params = new URLSearchParams();
            params.set('limit', '50');
            params.set('offset', String(off));
            if (debouncedQ) params.set('q', debouncedQ);

            const chipDef = FILTER_CHIPS.find((c) => c.key === chip);
            if (tab === 'archive') {
                params.set('status', 'archive');
            } else if (chipDef?.status) {
                params.set('status', chipDef.status);
            }

            if (chip === 'today') {
                const start = new Date();
                start.setHours(0, 0, 0, 0);
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                params.set('deadline_from', start.toISOString());
                params.set('deadline_to', end.toISOString());
            }

            if (filterPersonId) {
                if (isCreatedView) {
                    params.set('assignee_id', String(filterPersonId));
                } else {
                    params.set('initiator_id', String(filterPersonId));
                }
            }

            const endpoints = tab === 'archive'
                ? ['/tasks/my', '/tasks/created-by-me']
                : [listEndpoint];

            const responses = await Promise.all(
                endpoints.map((endpoint) =>
                    apiFetch(`${API_URL}${endpoint}?${params}`)
                )
            );

            if (responses.some((r) => !r.ok)) throw new Error('load_failed');

            const payloads = await Promise.all(responses.map((r) => r.json()));

            let list = payloads.flatMap((data) =>
                Array.isArray(data?.items) ? data.items : []
            );

            list = Array.from(
                new Map(list.map((task) => [task.id, task])).values()
            );

            if (tab === 'created') {
                list = list.filter((t) => !isSelfAssignedTask(t, employeeId));
            }

            if (tab !== 'archive') {
                list = list.filter((t) => t.status !== 'archive');
            }

            if (chip === 'overdue') {
                list = list.filter((t) => t.is_overdue);
            }
            if (chip === 'important') {
                list = list.filter((t) => t.is_important);
            }
            if (chip === 'no_deadline') {
                list = list.filter((t) => !t.deadline_at);
            }

            setItems((prev) => (reset ? list : [...prev, ...list]));
            setHasMore(payloads.some((data) => data?.has_more));
            setOffset(Math.max(...payloads.map((data) => data?.next_offset ?? off + list.length)));
        } catch (e) {
            setError('Не удалось загрузить задачи');
            if (reset) setItems([]);
        } finally {
            setLoading(false);
        }
    }, [tab, chip, debouncedQ, offset, listEndpoint, filterPersonId, isCreatedView, employeeId]);

    const patchListItem = useCallback((taskId, patch) => {
        setItems((prev) => prev.map((t) => (
            Number(t.id) === Number(taskId) ? { ...t, ...patch } : t
        )));
    }, []);

    const applyTaskDetail = useCallback((task) => {
        const normalizedTask = normalizeTaskDetail(task);
        setDetail(normalizedTask);
        patchListItem(normalizedTask.id, taskToListPatch(normalizedTask));
    }, [patchListItem]);

    const markTaskCommentsRead = useCallback(async (taskId, upToCommentId = null) => {
        if (!taskId) return null;
        try {
            const result = await markTaskCommentsReadRequest(taskId, upToCommentId);
            patchListItem(taskId, { unread_comments_count: 0 });
            setDetail((prev) => {
                if (!prev || Number(prev.id) !== Number(taskId)) return prev;
                const reads = { ...(prev.participants_last_reads || {}) };
                if (employeeId != null && result?.last_read_comment_id != null) {
                    reads[String(employeeId)] = result.last_read_comment_id;
                }
                return {
                    ...prev,
                    unread_comments_count: 0,
                    participants_last_reads: reads,
                };
            });
            return result;
        } catch {
            return null;
        }
    }, [employeeId, patchListItem]);

    const resetDetailScrollState = useCallback(() => {
        shouldStickDetailTopRef.current = false;
        forceScrollToTopOnNextCommentRef.current = false;
        commentsPagingArmedRef.current = false;
    }, []);

    const scrollDetailToTop = useCallback(() => {
        requestAnimationFrame(() => {
            const node = getDetailScrollEl();
            if (node) node.scrollTop = 0;
        });
    }, [getDetailScrollEl]);

    const scrollDetailToTopSettled = useCallback(() => {
        const scrollToTop = () => {
            requestAnimationFrame(() => {
                const node = getDetailScrollEl();
                if (node) node.scrollTop = 0;
            });
        };
        scrollToTop();
        [80, 220, 500, 900].forEach((delay) => {
            window.setTimeout(scrollToTop, delay);
        });
    }, [getDetailScrollEl]);

    const loadTaskCommentsPage = useCallback(async (taskId, options = {}) => {
        const { reset = false } = options;
        const numericTaskId = Number(taskId);
        if (!Number.isFinite(numericTaskId) || numericTaskId <= 0) return;
        if (commentsLoadingRef.current) return;
        commentsLoadingRef.current = true;
        setCommentsLoading(true);
        try {
            if (reset) {
                let beforeId = null;
                let hasMore = true;
                let guard = 0;
                let fullAsc = [];
                let participantsLastReads = null;

                while (hasMore && guard < 500) {
                    const params = new URLSearchParams();
                    params.set('limit', String(TASK_COMMENTS_PAGE_SIZE));
                    if (beforeId) params.set('before_id', String(beforeId));
                    const r = await apiFetch(`${API_URL}/tasks/${numericTaskId}/comments?${params.toString()}`);
                    if (!r.ok) throw new Error('task_comments_page_failed');
                    const data = await r.json();
                    const pageItems = Array.isArray(data?.items) ? data.items : [];
                    if (!participantsLastReads && data?.participants_last_reads) {
                        participantsLastReads = data.participants_last_reads;
                    }
                    fullAsc = [...pageItems, ...fullAsc];
                    hasMore = Boolean(data?.has_more);
                    beforeId = data?.next_before_id ?? null;
                    guard += 1;
                    if (!hasMore || !beforeId) break;
                }

                commentsTimelineRef.current = fullAsc;
                const forceToLatest = forceScrollToTopOnNextCommentRef.current;
                commentsLoadedCountRef.current = forceToLatest
                    ? fullAsc.length
                    : Math.min(TASK_COMMENTS_PAGE_SIZE, fullAsc.length);
                const visible = buildVisibleCommentsDesc(fullAsc, commentsLoadedCountRef.current);
                setCommentsHasMore(commentsLoadedCountRef.current < fullAsc.length);
                setDetail((prev) => {
                    if (!prev || Number(prev.id) !== numericTaskId) return prev;
                    return {
                        ...prev,
                        comments: visible,
                        participants_last_reads: participantsLastReads || prev.participants_last_reads || {},
                    };
                });
                if (forceToLatest) {
                    shouldStickDetailTopRef.current = true;
                    forceScrollToTopOnNextCommentRef.current = false;
                    scrollDetailToTopSettled();
                } else {
                    requestAnimationFrame(() => {
                        const node = isMobileTabViewport()
                            ? (detailMobileScrollRef.current || detailScrollRef.current)
                            : detailScrollRef.current;
                        if (node) node.scrollTop = 0;
                    });
                }
            } else {
                const all = Array.isArray(commentsTimelineRef.current) ? commentsTimelineRef.current : [];
                const loaded = Number(commentsLoadedCountRef.current) || 0;
                if (loaded >= all.length) {
                    setCommentsHasMore(false);
                    return;
                }
                commentsAppendingPageRef.current = true;
                const nextLoaded = Math.min(all.length, loaded + TASK_COMMENTS_PAGE_SIZE);
                commentsLoadedCountRef.current = nextLoaded;
                const visible = buildVisibleCommentsDesc(all, nextLoaded);
                setDetail((prev) => {
                    if (!prev || Number(prev.id) !== numericTaskId) return prev;
                    return { ...prev, comments: visible };
                });
                setCommentsHasMore(nextLoaded < all.length);
                requestAnimationFrame(() => {
                    commentsAppendingPageRef.current = false;
                });
            }
        } catch {
            // ignore pagination errors to avoid disrupting detail view
        } finally {
            commentsLoadingRef.current = false;
            setCommentsLoading(false);
        }
    }, [scrollDetailToTopSettled]);

    const markNotificationsRead = useCallback(async (notificationIds, markAll = false) => {
        try {
            const r = await apiFetch(`${API_URL}/tasks/notifications/read`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    markAll
                        ? { mark_all: true }
                        : { notification_ids: notificationIds }
                ),
            });
            return r.ok;
        } catch {
            return false;
        }
    }, []);

    const markNotificationsReadForTask = useCallback(async (taskId) => {
        const numericTaskId = Number(taskId);
        if (!Number.isFinite(numericTaskId) || numericTaskId <= 0) return false;
        try {
            const params = new URLSearchParams({ limit: '100', unread_only: 'true' });
            const r = await apiFetch(`${API_URL}/tasks/notifications?${params.toString()}`);
            if (!r.ok) return false;
            const data = await r.json();
            const list = Array.isArray(data?.items) ? data.items : [];
            const relevant = list.filter((n) => !n.is_read && Number(n.task_id) === numericTaskId);
            const ids = relevant
                .map((n) => Number(n.id))
                .filter((id) => Number.isFinite(id) && id > 0);
            if (!ids.length) return true;
            const ok = await markNotificationsRead(ids);
            if (ok) {
                const idSet = new Set(ids);
                const visibleReadCount = relevant.filter((n) => !shouldHideTaskNotificationFromFeed(n)).length;
                setNotifications((prev) => prev.map((n) => (
                    idSet.has(Number(n.id)) ? { ...n, is_read: true } : n
                )));
                setUnreadNotifCount((c) => Math.max(0, c - visibleReadCount));
            }
            return ok;
        } catch {
            return false;
        }
    }, [markNotificationsRead]);

    const loadDetail = useCallback(async (taskId, options = {}) => {
        const { silent = false, markRead = true, markNotificationsRead = true } = options;
        if (!taskId) return;
        resetDetailScrollState();
        scrollDetailToTop();
        if (!silent) setDetailLoading(true);
        try {
            const r = await apiFetch(`${API_URL}/tasks/${taskId}`);
            if (!r.ok) throw new Error('detail_failed');
            const task = normalizeTaskDetail(await r.json());
            commentsTaskIdRef.current = Number(task.id);
            commentsBeforeIdRef.current = null;
            commentsLoadingRef.current = false;
            commentsPagingArmedRef.current = false;
            commentsTimelineRef.current = [];
            commentsLoadedCountRef.current = 0;
            commentsAppendingPageRef.current = false;
            setCommentsHasMore(false);
            setCommentsLoading(false);
            setDetail({ ...task, comments: [] });
            patchListItem(task.id, taskToListPatch(task));
            await loadTaskCommentsPage(task.id, { reset: true });
            if (markRead) await markTaskCommentsRead(taskId, null);
            if (markNotificationsRead) await markNotificationsReadForTask(taskId);
        } catch {
            if (!silent) setDetail(null);
        } finally {
            if (!silent) setDetailLoading(false);
        }
    }, [patchListItem, markTaskCommentsRead, loadTaskCommentsPage, markNotificationsReadForTask, resetDetailScrollState, scrollDetailToTop]);

    const fetchUnreadNotifCount = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: '100', unread_only: 'true' });
            const r = await apiFetch(`${API_URL}/tasks/notifications?${params.toString()}`);
            if (!r.ok) return;
            const data = await r.json();
            const list = filterTaskNotificationsForFeed(data?.items);
            setUnreadNotifCount(list.filter((n) => !n.is_read).length);
        } catch {
            /* ignore */
        }
    }, []);

    const loadNotifications = useCallback(async (reset = true) => {
        setNotifLoading(true);
        try {
            const params = new URLSearchParams({ limit: '30' });
            if (!reset && notifBeforeId) params.set('before_id', String(notifBeforeId));
            const r = await apiFetch(`${API_URL}/tasks/notifications?${params.toString()}`);
            if (!r.ok) throw new Error('notifications_failed');
            const data = await r.json();
            const list = filterTaskNotificationsForFeed(data?.items);
            setNotifications((prev) => (reset ? list : [...prev, ...list]));
            setNotifHasMore(Boolean(data?.has_more));
            setNotifBeforeId(data?.next_before_id ?? null);
            if (reset) {
                setUnreadNotifCount(list.filter((n) => !n.is_read).length);
            }
        } catch {
            if (reset) setNotifications([]);
        } finally {
            setNotifLoading(false);
        }
    }, [notifBeforeId]);

    const handleNotificationsReadSocket = useCallback((payload) => {
        if (payload?.mark_all) {
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadNotifCount(0);
            return;
        }
        const ids = Array.isArray(payload?.notification_ids)
            ? payload.notification_ids.map(Number).filter(Number.isFinite)
            : [];
        if (!ids.length) return;
        const idSet = new Set(ids);
        setNotifications((prev) => prev.map((n) => (
            idSet.has(Number(n.id)) ? { ...n, is_read: true } : n
        )));
        setUnreadNotifCount((c) => Math.max(0, c - idSet.size));
    }, []);

    useEffect(() => {
        notifPanelOpenRef.current = notifPanelOpen;
    }, [notifPanelOpen]);

    useEffect(() => {
        fetchUnreadNotifCount();
    }, [fetchUnreadNotifCount, employeeId]);

    useEffect(() => {
        if (!notifPanelOpen) return;
        setNotifBeforeId(null);
        loadNotifications(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifPanelOpen]);

    useEffect(() => {
        setOffset(0);
        loadList(true, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, chip, debouncedQ, employeeId, listEndpoint, filterPersonId]);

    useEffect(() => {
        fetchTabCounts();
    }, [fetchTabCounts, employeeId]);

    useEffect(() => {
        loadRepeatingTasks();
    }, [loadRepeatingTasks, employeeId]);

    useEffect(() => {
        const taskParam = searchParams.get('task');
        if (taskParam) {
            const id = Number(taskParam);
            if (Number.isFinite(id) && id > 0) {
                const fromPush = searchParams.get('from_push');
                const markNotificationsRead = String(fromPush) !== '1';
                setDetailId(id);
                loadDetail(id, { markNotificationsRead });
            }
        }
    }, [searchParams, loadDetail]);

    useEffect(() => {
        const token = getAccessToken();
        if (!token || !employeeId) return undefined;

        const socket = io(SOCKET_URL, {
            path: '/ws/socket.io',
            transports: ['websocket'],
            withCredentials: true,
            auth: (cb) => cb({
              token: getAccessToken(),
              device_id: getRangiDeviceId(),
            }),
        });

        const handleTaskSocketUpdate = (payload, options = {}) => {
            const eventType = String(payload?.type || '').toLowerCase();

            if (eventType === 'task_notifications_read') {
                handleNotificationsReadSocket(payload);
                return;
            }

            if (payload?.notification_id) {
                fetchUnreadNotifCount();
                if (notifPanelOpenRef.current) {
                    setNotifBeforeId(null);
                    loadNotifications(true);
                }
            }

            const isCommentEvent = ['task_comment', 'task_comment_updated', 'task_comment_deleted'].includes(eventType);
            const forceByPayload = eventType === 'task_comment' || eventType === 'task_comment_updated';
            const { forceScrollToTop = forceByPayload } = options;
            loadList(true);
            fetchTabCounts();
            loadRepeatingTasks();
            const tid = payload?.task_id ?? payload?.task_ids?.[0];
            if (detailId && (Number(tid) === Number(detailId) || payload?.task_ids?.includes(detailId))) {
                if (forceScrollToTop) {
                    forceScrollToTopOnNextCommentRef.current = true;
                }
                if (isCommentEvent) {
                    loadTaskCommentsPage(detailId, { reset: true });
                    markTaskCommentsRead(detailId, null);
                    return;
                }
                const suppress = (
                    Number(skipDetailRefreshRef.current.taskId) === Number(detailId)
                    && Date.now() < Number(skipDetailRefreshRef.current.until || 0)
                );
                if (suppress) return;
                loadDetail(detailId, { silent: true, markRead: true });
            }
        };

        const handleTaskCommentsRead = (payload) => {
            if (!payload?.task_id) return;
            const tid = Number(payload.task_id);
            const readerId = payload.employee_id;

            if (readerId != null && Number(readerId) === Number(employeeId)) {
                patchListItem(tid, { unread_comments_count: 0 });
            }

            setDetail((prev) => {
                if (!prev || Number(prev.id) !== tid) return prev;
                const reads = { ...(prev.participants_last_reads || {}) };
                if (readerId != null) {
                    reads[String(readerId)] = payload.last_read_comment_id ?? 0;
                }
                const next = { ...prev, participants_last_reads: reads };
                if (readerId != null && Number(readerId) === Number(employeeId)) {
                    next.unread_comments_count = 0;
                }
                return next;
            });
        };

        socket.on('tasks:update', (payload) => handleTaskSocketUpdate(payload));
        socket.on('task_comment_updated', (payload) => handleTaskSocketUpdate(payload, { forceScrollToTop: true }));
        socket.on('task_comment_deleted', (payload) => handleTaskSocketUpdate(payload));
        socket.on('task_comments_read', handleTaskCommentsRead);

        return () => socket.disconnect();
    }, [employeeId, detailId, loadList, loadDetail, loadTaskCommentsPage, markTaskCommentsRead, fetchTabCounts, patchListItem, loadRepeatingTasks, fetchUnreadNotifCount, loadNotifications, handleNotificationsReadSocket]);

    useEffect(() => {
        if (!assigneeQuery.trim()) {
            setEmployeeSearchResults([]);
            return undefined;
        }
        const t = setTimeout(async () => {
            try {
                const r = await apiFetch(
                    `${API_URL}/employees/search/?query=${encodeURIComponent(assigneeQuery.trim())}`
                );
                setEmployeeSearchResults(r.ok ? await r.json() : []);
            } catch {
                setEmployeeSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [assigneeQuery]);

    useEffect(() => {
        if (!personFilterOpen || !personFilterQuery.trim()) {
            setPersonFilterResults([]);
            return undefined;
        }
        const t = setTimeout(async () => {
            try {
                const r = await apiFetch(
                    `${API_URL}/employees/search/?query=${encodeURIComponent(personFilterQuery.trim())}`
                );
                setPersonFilterResults(r.ok ? await r.json() : []);
            } catch {
                setPersonFilterResults([]);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [personFilterOpen, personFilterQuery]);

    useEffect(() => {
        if (!personFilterOpen) return undefined;
        const onDocClick = (e) => {
            if (!personFilterAnchorRef.current?.contains(e.target)) {
                setPersonFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [personFilterOpen]);

    const clearFilterPerson = () => {
        setFilterPersonId(null);
        setFilterPersonName('');
        setPersonFilterQuery('');
        setPersonFilterResults([]);
        setPersonFilterOpen(false);
    };

    const togglePersonFilterOpen = () => {
        setPersonFilterOpen((open) => !open);
    };

    const handleChipChange = (key) => {
        if (chip === key) {
            setChip('all');
            return;
        }
        setChip(key);
    };

    const selectFilterPerson = (emp) => {
        if (!emp?.id) return;
        setFilterPersonId(emp.id);
        setFilterPersonName(emp.name || emp.full_name || `#${emp.id}`);
        setPersonFilterQuery('');
        setPersonFilterResults([]);
        setPersonFilterOpen(false);
    };

    const assigneeSuggestions = useMemo(() => {
        const q = assigneeQuery.trim().toLowerCase();
        if (!q) return [];

        const items = [];

        departments
            .filter((d) => !selectedDeptIds.includes(d.id))
            .filter((d) => matchesAssigneeQuery(d.name || d.title, q))
            .forEach((d) => {
                items.push({
                    kind: 'department',
                    id: d.id,
                    label: d.name || d.title || `Отдел #${d.id}`,
                    data: d,
                });
            });

        teams
            .filter((t) => !selectedTeamIds.includes(t.id))
            .filter((t) => matchesAssigneeQuery(t.name, q))
            .forEach((t) => {
                items.push({
                    kind: 'team',
                    id: t.id,
                    label: t.name || `Команда #${t.id}`,
                    data: t,
                });
            });

        ASSIGNEE_SIDE_OPTIONS
            .filter((s) => !selectedSides.includes(s.id))
            .filter((s) => matchesSideOption(s, q))
            .forEach((s) => {
                items.push({
                    kind: 'side',
                    id: s.id,
                    label: s.label,
                });
            });

        (Array.isArray(employeeSearchResults) ? employeeSearchResults : [])
            .filter((emp) => !selectedAssignees.some((x) => x.id === emp.id))
            .forEach((emp) => {
                items.push({
                    kind: 'employee',
                    id: emp.id,
                    label: emp.name || emp.full_name || `#${emp.id}`,
                    data: emp,
                });
            });

        return items;
    }, [
        assigneeQuery,
        departments,
        teams,
        employeeSearchResults,
        selectedDeptIds,
        selectedTeamIds,
        selectedAssignees,
        selectedSides,
    ]);

    const createDeadlineAt = useMemo(
        () => buildDeadlineIso(createDeadlineDate, createDeadlineTime),
        [createDeadlineDate, createDeadlineTime]
    );

    const createRepeatNextRunAt = useMemo(
        () => buildDateTimeIso(createRepeatDate, createRepeatTime),
        [createRepeatDate, createRepeatTime]
    );

    const currentEmployeeAssignee = useMemo(() => {
        if (employeeId == null || employeeId === '') return null;
        return {
            id: Number(employeeId),
            name: me?.full_name || undefined,
            full_name: me?.full_name || undefined,
        };
    }, [employeeId, me?.full_name]);

    const isSelfAssignee = useMemo(
        () => currentEmployeeAssignee != null
            && selectedAssignees.some((x) => Number(x.id) === Number(currentEmployeeAssignee.id)),
        [selectedAssignees, currentEmployeeAssignee]
    );

    const toggleAssignToSelf = useCallback(() => {
        if (!currentEmployeeAssignee) return;
        if (isSelfAssignee) {
            setSelectedAssignees((prev) => prev.filter(
                (x) => Number(x.id) !== Number(currentEmployeeAssignee.id)
            ));
        } else {
            setSelectedAssignees((prev) => (
                prev.some((x) => Number(x.id) === Number(currentEmployeeAssignee.id))
                    ? prev
                    : [...prev, currentEmployeeAssignee]
            ));
        }
    }, [currentEmployeeAssignee, isSelfAssignee]);

    const hasAssigneeSelection = useMemo(
        () =>
            selectedAssignees.length > 0
            || selectedDeptIds.length > 0
            || selectedTeamIds.length > 0
            || selectedSides.length > 0,
        [selectedAssignees, selectedDeptIds, selectedTeamIds, selectedSides]
    );

    const canCreateTask = useMemo(
        () =>
            Boolean(createTitle.trim())
            && hasAssigneeSelection
            && (!createRepeat || Boolean(createRepeatNextRunAt)),
        [createTitle, hasAssigneeSelection, createRepeat, createRepeatNextRunAt]
    );

    const pickAssigneeSuggestion = (item) => {
        if (item.kind === 'employee') {
            if (!selectedAssignees.some((x) => x.id === item.data.id)) {
                setSelectedAssignees((prev) => [...prev, item.data]);
            }
        } else if (item.kind === 'department') {
            setSelectedDeptIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
        } else if (item.kind === 'team') {
            setSelectedTeamIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
        } else if (item.kind === 'side') {
            setSelectedSides((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
        }
        setAssigneeQuery('');
        setEmployeeSearchResults([]);
    };

    useEffect(() => {
        if (!createOpen) return undefined;
        let alive = true;
        (async () => {
            try {
                const [dRes, tRes] = await Promise.all([
                    apiFetch(`${API_URL}/structure/departments`),
                    apiFetch(`${API_URL}/structure/teams`),
                ]);
                if (!alive) return;
                setDepartments(dRes.ok ? await dRes.json() : []);
                setTeams(tRes.ok ? await tRes.json() : []);
            } catch { /* ignore */ }
        })();
        return () => { alive = false; };
    }, [createOpen]);

    const stats = useMemo(() => {
        const total = items.length;
        const done = items.filter((t) => ['done', 'accepted'].includes(t.status)).length;
        const overdue = items.filter((t) => t.is_overdue).length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        return { total, done, remaining: total - done, pct, overdue };
    }, [items]);

    const overdueItems = useMemo(() => items.filter((t) => t.is_overdue).slice(0, 2), [items]);
    const canManageRepeat = useCallback(
        (task) => Number(task?.initiator_employee_id) === Number(employeeId),
        [employeeId]
    );

    const openDetail = useCallback((task) => {
        const isNewTask = Number(detailId) !== Number(task.id);
        setDetailId(task.id);
        patchListItem(task.id, { unread_comments_count: 0 });
        if (isNewTask) {
            setDetail(null);
            loadDetail(task.id);
        } else {
            loadDetail(task.id, { silent: true });
        }
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('from_push');
            p.set('task', String(task.id));
            return p;
        }, { replace: false });
    }, [detailId, patchListItem, loadDetail, setSearchParams]);

    const openNotification = useCallback(async (notif) => {
        const taskId = Number(notif?.task_id);
        if (!Number.isFinite(taskId) || taskId <= 0) return;
        const notifId = Number(notif?.id);
        if (notifId && !notif.is_read) {
            const ok = await markNotificationsRead([notifId]);
            if (ok) {
                setNotifications((prev) => prev.map((n) => (
                    Number(n.id) === notifId ? { ...n, is_read: true } : n
                )));
                setUnreadNotifCount((c) => Math.max(0, c - 1));
            }
        }
        setNotifPanelOpen(false);
        openDetail({ id: taskId });
    }, [markNotificationsRead, openDetail]);

    const handleMarkAllNotificationsRead = useCallback(async () => {
        if (markAllNotifBusy) return;
        setMarkAllNotifBusy(true);
        try {
            const ok = await markNotificationsRead([], true);
            if (ok) {
                setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
                setUnreadNotifCount(0);
            }
        } finally {
            setMarkAllNotifBusy(false);
        }
    }, [markAllNotifBusy, markNotificationsRead]);

    const openImageLightbox = useCallback((images, index) => {
        setImageLightbox({ images, index });
    }, []);

    const closeDetail = () => {
        resetDetailScrollState();
        setDetailId(null);
        setDetail(null);
        setCommentsHasMore(false);
        setCommentsLoading(false);
        commentsBeforeIdRef.current = null;
        commentsLoadingRef.current = false;
        commentsTaskIdRef.current = null;
        commentsPagingArmedRef.current = false;
        commentsTimelineRef.current = [];
        commentsLoadedCountRef.current = 0;
        commentsAppendingPageRef.current = false;
        setDetailMenuOpen(false);
        setImageLightbox(null);
        setMediaPanelOpen(false);
        setEditOpen(false);
        setCommentText('');
        setCommentFiles([]);
        setEditingCommentId(null);
        setEditingCommentBusy(false);
        setDeletingCommentId(null);
        setCommentMenuOpenId(null);
        setDetailDragActive(false);
        detailDragCounterRef.current = 0;
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('task');
            return p;
        }, { replace: true });
    };

    const changeStatus = async (newStatus) => {
        if (!detailId || statusBusy || !detail) return;
        const prevStatus = detail.status;
        if (newStatus === prevStatus) return;

        setStatusBusy(true);
        setDetail((prev) => (prev ? { ...prev, status: newStatus } : prev));
        patchListItem(detailId, { status: newStatus });

        try {
            const r = await apiFetch(`${API_URL}/tasks/${detailId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: newStatus }),
            });
            if (!r.ok) throw new Error('status_failed');
            const payload = await r.json().catch(() => null);
            const statusPatch = pickStatusOnlyPatch(payload, newStatus);
            setDetail((prev) => (prev ? { ...prev, ...statusPatch } : prev));
            patchListItem(detailId, statusPatch);
            skipDetailRefreshRef.current = { taskId: detailId, until: Date.now() + 2000 };
            fetchTabCounts();
        } catch {
            setDetail((prev) => (prev ? { ...prev, status: prevStatus } : prev));
            patchListItem(detailId, { status: prevStatus });
            setError('Не удалось изменить статус');
        } finally {
            setStatusBusy(false);
        }
    };

    const toggleSubtask = async (subtask) => {
        if (!detailId || !detail) return;
        const nextDone = !subtask.is_done;
        const prevSubtasks = detail.subtasks || [];

        setDetail((prev) => {
            if (!prev) return prev;
            const subtasks = (prev.subtasks || []).map((s) => (
                s.id === subtask.id ? { ...s, is_done: nextDone } : s
            ));
            return { ...prev, subtasks };
        });
        patchListItem(detailId, countSubtasks(
            prevSubtasks.map((s) => (s.id === subtask.id ? { ...s, is_done: nextDone } : s))
        ));

        try {
            const r = await apiFetch(`${API_URL}/tasks/${detailId}/subtasks/${subtask.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_done: nextDone }),
            });
            if (!r.ok) throw new Error('subtask_failed');
            const updated = await r.json();
            setDetail((prev) => {
                if (!prev) return prev;
                const subtasks = (prev.subtasks || []).map((s) => (
                    s.id === updated.id ? { ...s, ...updated } : s
                ));
                return { ...prev, subtasks };
            });
            patchListItem(detailId, countSubtasks(
                (detail.subtasks || []).map((s) => (
                    s.id === subtask.id ? { ...s, is_done: updated.is_done } : s
                ))
            ));
        } catch {
            setDetail((prev) => (prev ? { ...prev, subtasks: prevSubtasks } : prev));
            patchListItem(detailId, countSubtasks(prevSubtasks));
        }
    };

    const editingComment = useMemo(() => {
        if (!editingCommentId) return null;
        return (detail?.comments || []).find((c) => Number(c.id) === Number(editingCommentId)) ?? null;
    }, [detail?.comments, editingCommentId]);

    useEffect(() => {
        const node = getDetailScrollEl();
        if (!node) return undefined;
        const updateStickBottom = (fromUserScroll = false) => {
            const hasScrollableContent = node.scrollHeight > node.clientHeight + 120;
            if (fromUserScroll || hasScrollableContent) {
                shouldStickDetailTopRef.current = node.scrollTop <= 96;
            }
            if (fromUserScroll) {
                commentsPagingArmedRef.current = true;
            }
            const nearBottom = (node.scrollHeight - node.scrollTop - node.clientHeight) <= 120;
            const currentTaskId = Number(detail?.id);
            if (
                commentsPagingArmedRef.current
                && nearBottom
                && commentsHasMore
                && !commentsLoadingRef.current
                && Number.isFinite(currentTaskId)
                && currentTaskId > 0
            ) {
                loadTaskCommentsPage(currentTaskId, { reset: false });
            }
        };
        updateStickBottom(false);
        const onScroll = () => updateStickBottom(true);
        node.addEventListener('scroll', onScroll, { passive: true });
        return () => node.removeEventListener('scroll', onScroll);
    }, [detailId, detail?.id, commentsHasMore, loadTaskCommentsPage, getDetailScrollEl]);

    useEffect(() => {
        const currentTaskId = Number(detail?.id);
        if (!Number.isFinite(currentTaskId) || currentTaskId <= 0) {
            prevDetailCommentsCountRef.current = 0;
            prevDetailIdRef.current = null;
            return;
        }
        if (Number(prevDetailIdRef.current) !== currentTaskId) {
            prevDetailIdRef.current = currentTaskId;
            prevDetailCommentsCountRef.current = Array.isArray(detail?.comments) ? detail.comments.length : 0;
            return;
        }
        const commentsCount = Array.isArray(detail?.comments) ? detail.comments.length : 0;
        const prevCount = Number(prevDetailCommentsCountRef.current) || 0;
        const isInitialCommentsHydrate = prevCount === 0 && commentsCount > 0;
        if (
            commentsCount > prevCount
            && !isInitialCommentsHydrate
            && (
                forceScrollToTopOnNextCommentRef.current
                || (shouldStickDetailTopRef.current && !commentsAppendingPageRef.current)
            )
        ) {
            forceScrollToTopOnNextCommentRef.current = false;
            shouldStickDetailTopRef.current = true;
            scrollDetailToTopSettled();
        }
        prevDetailCommentsCountRef.current = commentsCount;
    }, [detail?.id, detail?.comments, scrollDetailToTopSettled]);

    const cancelEditComment = () => {
        if (editingCommentBusy) return;
        setEditingCommentId(null);
        setCommentText('');
    };

    const saveCommentEdit = async () => {
        const comment = editingComment;
        const text = commentText.trim();
        if (!detailId || !comment?.id || editingCommentBusy || !text) return;
        if (text === (comment.text || '').trim()) {
            cancelEditComment();
            return;
        }

        setEditingCommentBusy(true);
        try {
            const r = await apiFetch(`${API_URL}/tasks/${detailId}/comments/${comment.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ text }),
            });
            if (!r.ok) throw new Error('comment_edit_failed');
            const updated = await r.json();
            commentsTimelineRef.current = (commentsTimelineRef.current || []).map((c) => (
                Number(c.id) === Number(updated.id) ? updated : c
            ));
            setDetail((prev) => {
                if (!prev) return prev;
                const comments = buildVisibleCommentsDesc(
                    commentsTimelineRef.current,
                    commentsLoadedCountRef.current,
                );
                return { ...prev, comments };
            });
            setEditingCommentId(null);
            setCommentText('');
        } catch {
            setError('Не удалось изменить комментарий');
        } finally {
            setEditingCommentBusy(false);
        }
    };

    const focusTaskCommentInput = () => {
        const footers = document.querySelectorAll(`.${styles.detailFooter}`);
        for (const footer of footers) {
            if (footer.offsetParent === null) continue;
            const el = footer.querySelector('textarea');
            if (!el) continue;
            el.focus();
            const len = el.value.length;
            try {
                el.setSelectionRange(len, len);
            } catch { /* ignore */ }
            return;
        }
        commentInputRef.current?.focus();
    };

    const startEditComment = (comment) => {
        if (!comment?.id || editingCommentBusy) return;
        setCommentMenuOpenId(null);
        setCommentFiles([]);
        setEditingCommentId(comment.id);
        setCommentText(comment.text || '');

        window.setTimeout(() => {
            const commentEl = document.getElementById(`task-comment-${comment.id}`);
            focusTaskCommentInput();

            window.setTimeout(() => {
                commentEl?.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                });
            }, 180);
        }, 0);
    };

    const renderDetailComposer = (footerClassName = '', composerRef = null) => (
        <div
            ref={composerRef}
            className={[styles.detailFooter, footerClassName].filter(Boolean).join(' ')}
        >
            {editingCommentId && (
                <div className={styles.commentEditComposerBar}>
                    <span className={styles.commentEditComposerLabel}>Редактирование</span>
                    <button
                        type="button"
                        className={styles.commentEditComposerCancel}
                        onClick={cancelEditComment}
                        disabled={editingCommentBusy}
                    >
                        Отмена
                    </button>
                </div>
            )}
            {!editingCommentId && commentFiles.length > 0 && (
                <div className={styles.commentPendingFiles}>
                    {commentFiles.map((file, idx) => (
                        <span
                            key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                            className={styles.commentPendingFile}
                        >
                            📎 {file.name}
                            <button
                                type="button"
                                className={styles.chipRemove}
                                onClick={() => removeCommentFile(idx)}
                                aria-label={`Убрать ${file.name}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className={`${styles.commentForm} ${editingCommentId ? styles.commentFormEditing : ''}`}>
                <div className={styles.commentFieldWrap}>
                    <Textarea
                        wrapperClassName={`${styles.commentField} ${editingCommentId ? styles.commentFieldEditing : ''}`}
                        className={styles.commentTextarea}
                        placeholder={editingCommentId ? 'Редактировать комментарий…' : 'Написать комментарий…'}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={handleCommentKeyDown}
                        onPaste={editingCommentId ? undefined : handleCommentPaste}
                        ariaLabel={editingCommentId ? 'Редактировать комментарий' : 'Комментарий'}
                        textareaRef={commentInputRef}
                        rows={editingCommentId ? 2 : 1}
                        maxRows={6}
                    />
                    {!editingCommentId && (
                        <label className={styles.commentAttachBtn} title="Прикрепить файл" aria-label="Прикрепить файл">
                            <svg className={styles.commentAttachIcon} width="13" height="22" viewBox="0 0 13 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M12 4.25581V15.5C12 18.5376 9.53757 21 6.5 21C3.46243 21 1 18.5376 1 15.5V4.66667C1 2.64162 2.64162 1 4.66667 1C6.69171 1 8.33333 2.64162 8.33333 4.66667V15.4457C8.33333 16.4583 7.51252 17.2791 6.5 17.2791C5.48748 17.2791 4.66667 16.4583 4.66667 15.4457V5.65116" stroke="#888888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <input
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                    mergeCommentFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                        </label>
                    )}
                </div>
                <button
                    type="button"
                    className={styles.sendBtn}
                    disabled={
                        commentBusy
                        || editingCommentBusy
                        || (editingCommentId
                            ? !commentText.trim()
                            : !commentText.trim() && !commentFiles.length)
                    }
                    onClick={sendComment}
                    aria-label={editingCommentId ? 'Сохранить' : 'Отправить'}
                >
                    <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.227694 1.2984C0.0462206 0.751643 -0.0445162 0.478266 0.0215284 0.309871C0.0788852 0.163628 0.201636 0.0528425 0.352976 0.0107326C0.527238 -0.0377556 0.78991 0.0804462 1.31525 0.31685L18.0325 7.83959C18.5469 8.07104 18.804 8.18676 18.8835 8.34752C18.9526 8.48719 18.9526 8.65107 18.8835 8.79073C18.804 8.9515 18.5469 9.06722 18.0325 9.29867L1.32108 16.8188C0.794148 17.0559 0.530681 17.1745 0.356245 17.1258C0.204764 17.0835 0.0820002 16.9725 0.024841 16.8259C-0.0409797 16.6572 0.0507372 16.3833 0.234171 15.8353L2.56909 8.86063C2.6005 8.7668 2.61621 8.71989 2.62248 8.67188C2.62805 8.62929 2.62811 8.58615 2.62265 8.54354C2.61649 8.49551 2.60091 8.44856 2.56974 8.35466L0.227694 1.2984Z" fill="black" />
                    </svg>
                </button>
            </div>
        </div>
    );

    const sendComment = async () => {
        if (editingCommentId) {
            await saveCommentEdit();
            return;
        }
        const text = commentText.trim();
        if (!detailId || commentBusy) return;
        if (!text && !commentFiles.length) return;
        setCommentBusy(true);
        try {
            let attachments = [];
            if (commentFiles.length) {
                attachments = await uploadFilesToTask(detailId, commentFiles, 'comment');
            }
            const r = await apiFetch(`${API_URL}/tasks/${detailId}/comments`, {
                method: 'POST',
                body: JSON.stringify({ text, attachments }),
            });
            if (!r.ok) throw new Error('comment_failed');
            const comment = await r.json();
            setCommentText('');
            setCommentFiles([]);
            const timeline = [...(commentsTimelineRef.current || []), comment];
            commentsTimelineRef.current = timeline;
            const prevLoaded = Number(commentsLoadedCountRef.current) || 0;
            if (prevLoaded >= timeline.length - 1) {
                commentsLoadedCountRef.current = timeline.length;
            } else {
                commentsLoadedCountRef.current = prevLoaded;
            }
            setCommentsHasMore(commentsLoadedCountRef.current < timeline.length);
            shouldStickDetailTopRef.current = true;
            setDetail((prev) => {
                if (!prev) return prev;
                const comments = buildVisibleCommentsDesc(
                    commentsTimelineRef.current,
                    commentsLoadedCountRef.current,
                );
                return { ...prev, comments };
            });
            scrollDetailToTopSettled();
            const nextCommentsCount = (detail.comments || []).length + 1;
            patchListItem(detailId, {
                comments_count: nextCommentsCount,
                unread_comments_count: 0,
            });
            setDetail((prev) => (
                prev ? { ...prev, unread_comments_count: 0 } : prev
            ));
        } catch {
            setError('Не удалось отправить комментарий');
        } finally {
            setCommentBusy(false);
        }
    };

    const handleCommentKeyDown = (e) => {
        if (editingCommentId && e.key === 'Escape') {
            e.preventDefault();
            cancelEditComment();
            return;
        }
        if (e.key !== 'Enter' || e.shiftKey) return;

        const isTouchInput = typeof window !== 'undefined'
            && window.matchMedia?.('(hover: none), (pointer: coarse)').matches;
        if (isTouchInput) return;

        e.preventDefault();
        if (editingCommentId) {
            if (editingCommentBusy) return;
            if (!commentText.trim()) return;
            saveCommentEdit();
            return;
        }
        if (commentBusy) return;
        const text = commentText.trim();
        if (!text && !commentFiles.length) return;
        sendComment();
    };

    const deleteComment = async (comment) => {
        if (!detailId || !comment?.id || deletingCommentId) return;
        setCommentMenuOpenId(null);
        if (!window.confirm('Удалить комментарий?')) return;

        setDeletingCommentId(comment.id);
        try {
            const r = await apiFetch(`${API_URL}/tasks/${detailId}/comments/${comment.id}`, {
                method: 'DELETE',
            });
            if (!r.ok) throw new Error('comment_delete_failed');
            commentsTimelineRef.current = (commentsTimelineRef.current || []).filter(
                (c) => Number(c.id) !== Number(comment.id),
            );
            commentsLoadedCountRef.current = Math.min(
                commentsLoadedCountRef.current,
                commentsTimelineRef.current.length,
            );
            const nextComments = buildVisibleCommentsDesc(
                commentsTimelineRef.current,
                commentsLoadedCountRef.current,
            );
            setCommentsHasMore(commentsLoadedCountRef.current < commentsTimelineRef.current.length);
            setDetail((prev) => (prev ? { ...prev, comments: nextComments } : prev));
            patchListItem(detailId, { comments_count: commentsTimelineRef.current.length });
            if (Number(editingCommentId) === Number(comment.id)) {
                setEditingCommentId(null);
                setCommentText('');
            }
        } catch {
            setError('Не удалось удалить комментарий');
        } finally {
            setDeletingCommentId(null);
        }
    };

    const deleteTask = async (deleteGroup = false) => {
        if (!detailId || !window.confirm(deleteGroup ? 'Удалить всю группу задач?' : 'Удалить задачу?')) return;
        setDetailMenuOpen(false);
        try {
            const qs = deleteGroup ? '?delete_group=true' : '';
            const r = await apiFetch(`${API_URL}/tasks/${detailId}${qs}`, { method: 'DELETE' });
            if (!r.ok) throw new Error('delete_failed');
            closeDetail();
            loadList(true);
            fetchTabCounts();
            loadRepeatingTasks();
        } catch {
            setError('Не удалось удалить задачу');
        }
    };

    const disableTaskRepeat = async (task) => {
        const sourceTaskId = Number(task?.repeat_rule?.source_task_id ?? task?.id);
        if (!sourceTaskId || repeatBusyId || Number(task?.initiator_employee_id) !== Number(employeeId)) return;
        setRepeatBusyId(sourceTaskId);
        try {
            const r = await apiFetch(`${API_URL}/tasks/${sourceTaskId}/repeat`, {
                method: 'PATCH',
                body: JSON.stringify({ is_active: false }),
            });
            if (!r.ok) throw new Error('repeat_disable_failed');

            setRepeatingItems((prev) => prev.filter((item) => (
                Number(item?.repeat_rule?.source_task_id ?? item?.id) !== sourceTaskId
            )));
            patchListItem(sourceTaskId, {
                repeat_rule: task?.repeat_rule ? { ...task.repeat_rule, is_active: false } : null,
            });
            setDetail((prev) => {
                if (!prev || Number(prev.id) !== sourceTaskId || !prev.repeat_rule) return prev;
                return { ...prev, repeat_rule: { ...prev.repeat_rule, is_active: false } };
            });
        } catch {
            setError('Не удалось отключить повторение');
        } finally {
            setRepeatBusyId(null);
        }
    };

    const openEdit = () => {
        if (!detail) return;
        setDetailMenuOpen(false);
        let deadlineDate = '';
        let deadlineTime = '';
        if (detail.deadline_at) {
            try {
                const d = new Date(detail.deadline_at);
                deadlineDate = localDateInputValue(d);
                deadlineTime = isDateOnlyDeadline(d) ? '' : d.toTimeString().slice(0, 5);
            } catch { /* ignore */ }
        }
        setEditTitle(detail.title || '');
        setEditTitleError('');
        setEditDesc(detail.description || '');
        setEditDeadlineDate(deadlineDate);
        setEditDeadlineTime(deadlineTime);
        setEditImportant(!!detail.is_important);
        setEditSubtasks(normalizeEditSubtasksDraft(detail.subtasks));
        setEditOpen(true);
    };

    const saveEdit = async () => {
        if (!detailId || editBusy || !editTitle.trim()) return;
        if (editTitle.trim().length > TASK_TITLE_MAX_LENGTH) {
            setEditTitleError(TASK_TITLE_TOO_LONG_MESSAGE);
            return;
        }
        setEditBusy(true);
        try {
            const r = await apiFetch(`${API_URL}/tasks/${detailId}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    title: editTitle.trim(),
                    description: editDesc.trim() || null,
                    deadline_at: buildDeadlineIso(editDeadlineDate, editDeadlineTime),
                    is_important: editImportant,
                    subtasks: buildEditSubtasksPayload(editSubtasks),
                }),
            });
            if (!r.ok) throw new Error('edit_failed');
            applyTaskDetail(await r.json());
            setEditOpen(false);
            fetchTabCounts();
        } catch {
            setError('Не удалось сохранить задачу');
        } finally {
            setEditBusy(false);
        }
    };

    const mergeCreateFiles = useCallback((incomingFiles) => {
        const nextFiles = Array.from(incomingFiles || []);
        if (!nextFiles.length) return;

        setCreateFiles((prev) => {
            const merged = [...prev];
            const seen = new Set(
                prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
            );

            for (const file of nextFiles) {
                const key = `${file.name}-${file.size}-${file.lastModified}`;
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(file);
            }

            return merged;
        });
    }, []);

    const removeCreateFile = useCallback((index) => {
        setCreateFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const dragHasFiles = useCallback((dt) => {
        if (!dt) return false;
        return Array.from(dt.types || []).includes('Files');
    }, []);

    const handleCreateModalDragEnter = useCallback((e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        createDragCounterRef.current += 1;
        setCreateDragActive(true);
    }, [dragHasFiles]);

    const handleCreateModalDragOver = useCallback((e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        try {
            e.dataTransfer.dropEffect = 'copy';
        } catch { /* ignore */ }
        setCreateDragActive(true);
    }, [dragHasFiles]);

    const handleCreateModalDragLeave = useCallback((e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        createDragCounterRef.current = Math.max(0, createDragCounterRef.current - 1);
        if (createDragCounterRef.current === 0) {
            setCreateDragActive(false);
        }
    }, [dragHasFiles]);

    const handleCreateModalDrop = useCallback((e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        createDragCounterRef.current = 0;
        setCreateDragActive(false);
        mergeCreateFiles(e.dataTransfer.files);
    }, [dragHasFiles, mergeCreateFiles]);

    const handleCreatePaste = useCallback((e) => {
        const files = filesFromClipboardEvent(e);
        if (!files.length) return;
        e.preventDefault();
        mergeCreateFiles(files);
    }, [mergeCreateFiles]);

    const mergeCommentFiles = useCallback((fileList) => {
        const nextFiles = Array.from(fileList || []).filter(Boolean);
        if (!nextFiles.length) return;

        setCommentFiles((prev) => {
            const merged = [...prev];
            const seen = new Set(
                prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
            );

            for (const file of nextFiles) {
                const key = `${file.name}-${file.size}-${file.lastModified}`;
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(file);
            }

            return merged;
        });
    }, []);

    const removeCommentFile = useCallback((index) => {
        setCommentFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleCommentPaste = useCallback((e) => {
        if (!detailId) return;

        const mediaFiles = filesFromClipboardEvent(e)
            .filter((file) => /^(image|video|audio)\//i.test(file.type || ''));

        if (!mediaFiles.length) return;
        e.preventDefault();
        mergeCommentFiles(mediaFiles);
    }, [detailId, mergeCommentFiles]);

    const handleDetailDragEnter = useCallback((e) => {
        if (!detailId || !dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        detailDragCounterRef.current += 1;
        setDetailDragActive(true);
    }, [detailId, dragHasFiles]);

    const handleDetailDragOver = useCallback((e) => {
        if (!detailId || !dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        try {
            e.dataTransfer.dropEffect = 'copy';
        } catch { /* ignore */ }
        setDetailDragActive(true);
    }, [detailId, dragHasFiles]);

    const handleDetailDragLeave = useCallback((e) => {
        if (!detailId || !dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        detailDragCounterRef.current = Math.max(0, detailDragCounterRef.current - 1);
        if (detailDragCounterRef.current === 0) {
            setDetailDragActive(false);
        }
    }, [detailId, dragHasFiles]);

    const handleDetailDrop = useCallback((e) => {
        if (!detailId || !dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.stopPropagation();
        detailDragCounterRef.current = 0;
        setDetailDragActive(false);
        mergeCommentFiles(e.dataTransfer.files);
    }, [detailId, dragHasFiles, mergeCommentFiles]);

    const createDraftSnapshotRef = useRef(null);
    const [createDraftHydrated, setCreateDraftHydrated] = useState(false);

    const flushCreateDraftToStorage = useCallback(() => {
        const snap = createDraftSnapshotRef.current;
        if (isTaskCreateDraftEmpty(snap)) {
            clearTaskCreateDraft(employeeId);
        } else {
            writeTaskCreateDraft(employeeId, snap);
        }
    }, [employeeId]);

    const closeCreateModal = useCallback(() => {
        flushCreateDraftToStorage();
        setCreateOpen(false);
        setCreateDragActive(false);
        createDragCounterRef.current = 0;
    }, [flushCreateDraftToStorage]);

    const resetCreateForm = useCallback(() => {
        setCreateTitle('');
        setCreateTitleError('');
        setCreateDesc('');
        setCreateDeadlineDate('');
        setCreateDeadlineTime('');
        setCreateImportant(false);
        setCreateRepeat('');
        setCreateRepeatDate('');
        setCreateRepeatTime('');
        setCreateSubtasks(['']);
        setCreateFiles([]);
        setCreateDragActive(false);
        createDragCounterRef.current = 0;
        setAssigneeQuery('');
        setEmployeeSearchResults([]);
        setSelectedAssignees([]);
        setSelectedDeptIds([]);
        setSelectedTeamIds([]);
        setSelectedSides([]);
    }, []);

    const applyCreateDraft = useCallback((draft) => {
        if (!draft || isTaskCreateDraftEmpty(draft)) {
            resetCreateForm();
            return;
        }
        setCreateTitle(String(draft.title ?? ''));
        setCreateDesc(String(draft.description ?? ''));
        setCreateDeadlineDate(String(draft.deadlineDate ?? ''));
        setCreateDeadlineTime(String(draft.deadlineTime ?? ''));
        setCreateImportant(Boolean(draft.important));
        setCreateRepeat(String(draft.repeat ?? ''));
        setCreateRepeatDate(String(draft.repeatDate ?? ''));
        setCreateRepeatTime(String(draft.repeatTime ?? ''));
        setCreateSubtasks(normalizeCreateSubtasksDraft(draft.subtasks));
        setCreateFiles([]);
        setCreateDragActive(false);
        createDragCounterRef.current = 0;
        setAssigneeQuery('');
        setEmployeeSearchResults([]);
        setSelectedAssignees(normalizeDraftAssignees(draft.assignees));
        setSelectedDeptIds(Array.isArray(draft.departmentIds) ? draft.departmentIds : []);
        setSelectedTeamIds(Array.isArray(draft.teamIds) ? draft.teamIds : []);
        setSelectedSides(Array.isArray(draft.sides) ? draft.sides : []);
    }, [resetCreateForm]);

    const openCreateModal = useCallback(() => {
        applyCreateDraft(readTaskCreateDraft(employeeId));
        setCreateOpen(true);
    }, [applyCreateDraft, employeeId]);

    useEffect(() => {
        if (employeeId == null || employeeId === '') return;
        applyCreateDraft(readTaskCreateDraft(employeeId));
        setCreateDraftHydrated(true);
    }, [employeeId, applyCreateDraft]);

    useMobileKeyboardAvoidance({
        active: Boolean(detailId),
        modalRef: detailModalRef,
        composerRef: detailComposerRef,
    });

    useMobileKeyboardAvoidance({
        active: createOpen,
        modalRef: createModalRef,
        composerRef: createModalRef,
        scrollFocusedField: true,
    });

    useMobileKeyboardAvoidance({
        active: editOpen,
        modalRef: editModalRef,
        composerRef: editModalRef,
        scrollFocusedField: true,
    });

    const createDraftSnapshot = useMemo(
        () => buildTaskCreateDraftSnapshot({
            title: createTitle,
            description: createDesc,
            deadlineDate: createDeadlineDate,
            deadlineTime: createDeadlineTime,
            important: createImportant,
            repeat: createRepeat,
            repeatDate: createRepeatDate,
            repeatTime: createRepeatTime,
            subtasks: createSubtasks,
            assignees: selectedAssignees,
            departmentIds: selectedDeptIds,
            teamIds: selectedTeamIds,
            sides: selectedSides,
        }),
        [
            createTitle,
            createDesc,
            createDeadlineDate,
            createDeadlineTime,
            createImportant,
            createRepeat,
            createRepeatDate,
            createRepeatTime,
            createSubtasks,
            selectedAssignees,
            selectedDeptIds,
            selectedTeamIds,
            selectedSides,
        ]
    );

    createDraftSnapshotRef.current = createDraftSnapshot;

    useEffect(() => {
        if (!createDraftHydrated) return undefined;
        if (isTaskCreateDraftEmpty(createDraftSnapshot)) {
            clearTaskCreateDraft(employeeId);
            return undefined;
        }
        const timer = window.setTimeout(() => {
            flushCreateDraftToStorage();
        }, 400);
        return () => {
            window.clearTimeout(timer);
            flushCreateDraftToStorage();
        };
    }, [createDraftHydrated, createDraftSnapshot, employeeId, flushCreateDraftToStorage]);

    const handleCreate = async () => {
        if (createBusy || !canCreateTask) return;
        if (createTitle.trim().length > TASK_TITLE_MAX_LENGTH) {
            setCreateTitleError(TASK_TITLE_TOO_LONG_MESSAGE);
            return;
        }
        setCreateBusy(true);
        try {
            const subtasks = createSubtasks
                .map((t, i) => ({ title: t.trim(), is_done: false, position: i }))
                .filter((s) => s.title);

            let attachments = [];
            if (createFiles.length) {
                const presignTaskId = Number(detailId) || Number(items?.[0]?.id);
                if (!presignTaskId) {
                    throw new Error('create_presign_task_required');
                }
                attachments = await uploadFilesToTask(presignTaskId, createFiles, 'task');
            }

            const payload = {
                title: createTitle.trim(),
                description: createDesc.trim() || null,
                deadline_at: createDeadlineAt,
                is_important: createImportant,
                assignee_employee_ids: selectedAssignees.map((e) => e.id),
                department_ids: selectedDeptIds,
                team_ids: selectedTeamIds,
                sides: selectedSides,
                subtasks,
                attachments,
                repeat: createRepeat
                    ? { frequency: createRepeat, next_run_at: createRepeatNextRunAt }
                    : null,
            };

            const r = await apiFetch(`${API_URL}/tasks`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error('create_failed');
            const created = await r.json();
            const first = Array.isArray(created) ? created[0] : null;
            setCreateOpen(false);
            clearTaskCreateDraft(employeeId);
            resetCreateForm();
            loadList(true);
            fetchTabCounts();
            if (first?.id) openDetail(first);
        } catch (e) {
            if (e?.message === 'create_presign_task_required') {
                setError('Нельзя прикрепить файл к первой задаче: сначала создайте задачу без файла.');
            } else {
                setError('Не удалось создать задачу');
            }
        } finally {
            setCreateBusy(false);
        }
    };

    const isMyTasksView = tab === 'my';
    const isCreatedTasksView = tab === 'created';
    const hasSidebarWidgets = isMyTasksView || (isCreatedTasksView && repeatingItems.length > 0);
    const mobileRepeatingItems = showAllMobileRepeats ? repeatingItems : repeatingItems.slice(0, 3);

    const sortedItems = useMemo(() => {
        const column = tableSort.column;
        if (!column || !TABLE_SORT_COLUMNS.includes(column)) return items;
        return [...items].sort((a, b) => compareTasksForTable(
            a,
            b,
            column,
            tableSort.direction,
            isCreatedView
        ));
    }, [items, tableSort, isCreatedView]);

    const handleTableSort = useCallback((column) => {
        if (!TABLE_SORT_COLUMNS.includes(column)) return;
        setTableSort((prev) => {
            if (prev.column === column) {
                return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { column, direction: 'asc' };
        });
    }, []);

    const renderTableSortHeader = useCallback((column, label) => {
        const isActive = tableSort.column === column;
        const ariaSort = isActive
            ? (tableSort.direction === 'asc' ? 'ascending' : 'descending')
            : 'none';
        return (
            <th
                key={column}
                className={`${styles.thSortable} ${isActive ? styles.thSortActive : ''}`}
                onClick={() => handleTableSort(column)}
                aria-sort={ariaSort}
            >
                <span className={styles.thSortInner}>
                    <span>{label}</span>
                    <span className={styles.thSortIcon} aria-hidden="true">
                        {isActive ? (tableSort.direction === 'asc' ?
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 15L12 9L6 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg> :
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>) :
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 15L12 20L17 15M7 9L12 4L17 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        }
                    </span>
                </span>
            </th>
        );
    }, [tableSort, handleTableSort]);

    const handleTabChange = (key) => {
        if (key === tab) return;
        if (key !== 'archive') setListMode(key);
        setItems([]);
        setHasMore(false);
        setOffset(0);
        setLoading(true);
        setTab(key);
        setPersonFilterOpen(false);
        setMobileFiltersOpen(false);
        setShowAllMobileRepeats(false);
    };

    const renderCard = (task) => {
        const { person, personId } = getTaskCounterpartyMeta(task, employeeId);
        const subProgress = task.subtasks_total
            ? `${task.subtasks_done}/${task.subtasks_total}`
            : null;
        const unreadCommentsCount = getUnreadCommentsCount(task);

        return (
            <li key={task.id}>
                <article className={styles.card} onClick={() => openDetail(task)} role="button" tabIndex={0}>
                    <div className={styles.cardTop}>
                        <h3 className={styles.cardTitle}>
                            <span className={styles.cardTitleText}>
                                {task.is_important && <span className={styles.importantTag}>Важно </span>}
                                {task.title}
                            </span>
                            {unreadCommentsCount > 0 && (
                                <span className={styles.cardCommentBadge} aria-label={`Новых комментариев: ${unreadCommentsCount}`}>
                                    {unreadCommentsCount > 99 ? '99+' : unreadCommentsCount}
                                </span>
                            )}
                        </h3>
                        <span className={styles.cardTime}>{formatRelativeTime(task.created_at)}</span>
                    </div>
                    {subProgress && <div className={styles.progressHint}>Подзадачи {subProgress}</div>}
                    <div className={styles.cardBottom}>
                        <span
                            className={`${styles.cardDeadline} ${isDeadlineUrgent(task) ? styles.cardDeadlineOverdue : ''}`}
                        >
                            {formatDeadline(task.deadline_at)}
                        </span>
                        <div className={styles.cardMeta}>
                            <StatusBadge status={task.status} />
                            {person && (
                                <TaskAvatar
                                    employeeId={person.id ?? personId}
                                    thumb={person.avatar_thumb}
                                    avatar={person.avatar}
                                    alt={person.name || ''}
                                    statsMap={employeeTaskStatsMap}
                                />
                            )}
                        </div>
                    </div>
                </article>
            </li>
        );
    };

    const statusSelectOptions = detail ? getStatusSelectOptionsForDetail(detail, employeeId) : [];
    const canChangeTaskStatus = detail
        && statusSelectOptions.some((s) => s !== detail.status);
    const statusSelectTone = STATUS_META[detail?.status]?.selectCls || '';
    const canManageTask = detail
        && Number(detail.initiator_employee_id) === Number(employeeId);

    const subtasksDone = detail?.subtasks?.filter((s) => s.is_done).length || 0;
    const subtasksTotal = detail?.subtasks?.length || 0;
    const subPct = subtasksTotal ? Math.round((subtasksDone / subtasksTotal) * 100) : 0;
    const hasMediaAttachments = Boolean(
        (detail?.attachments?.length || 0) > 0
        || (detail?.comments || []).some((comment) => (comment?.attachments?.length || 0) > 0)
    );

    const detailCounterpartyMeta = detail ? getTaskCounterpartyMeta(detail, employeeId) : null;
    const detailCounterparty = detail
        ? pickTaskEmployee(detail, detailCounterpartyMeta.personId)
        : null;
    const detailCounterpartyLabel = detailCounterpartyMeta?.label || 'Инициатор';

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headerTop}>
                    <div className={styles.headerLeft}>
                        <h1 className={styles.title}>Задачи</h1>
                    </div>
                    <div className={styles.headerActions}>
                        <button
                            type="button"
                            className={`${styles.notifBellBtn} ${notifPanelOpen ? styles.notifBellBtnActive : ''}`}
                            onClick={() => setNotifPanelOpen(true)}
                            aria-label="Лента событий"
                            aria-expanded={notifPanelOpen}
                        >
                            <TaskBellIcon />
                            {unreadNotifCount > 0 && (
                                <span className={styles.notifBellBadge} aria-hidden="true">
                                    {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            className={`${styles.headerIconBtn} ${mobileFiltersOpen ? styles.headerIconBtnActive : ''}`}
                            onClick={() => setMobileFiltersOpen((v) => !v)}
                            aria-label="Фильтры"
                            aria-expanded={mobileFiltersOpen}
                        >
                            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M4 6H20L14 13V19L10 21V13L4 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`${styles.headerIconBtn} ${mobileSearchOpen ? styles.headerIconBtnActive : ''}`}
                            onClick={() => setMobileSearchOpen((v) => !v)}
                            aria-label="Поиск"
                            aria-expanded={mobileSearchOpen}
                        >
                            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button type="button" className={styles.newBtn} onClick={openCreateModal}>
                            <span className={styles.newBtnText}>Новая задача</span>
                            <span className={styles.newBtnPlus} aria-hidden="true">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 1V15M1 8H15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
                <div
                    ref={tabsBarRef}
                    className={styles.tabsBar}
                    onMouseDown={handleTabsMouseDown}
                >
                    {TABS.map((t) => {
                        const count = tabCounts[t.key] ?? 0;
                        const isActive = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                ref={(node) => {
                                    if (node) {
                                        tabButtonRefs.current[t.key] = node;
                                    } else {
                                        delete tabButtonRefs.current[t.key];
                                    }
                                }}
                                type="button"
                                className={`${styles.tabBtn} ${isActive ? styles.tabBtnActive : ''}`}
                                onClick={() => {
                                    if (tabsSuppressClickRef.current) return;
                                    handleTabChange(t.key);
                                }}
                            >
                                <span className={styles.tabLabel}>{t.label}</span>
                                {count > 0 && (
                                    <span className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : ''}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    <span
                        className={styles.tabsIndicator}
                        style={{
                            transform: `translateX(${tabIndicator.left}px)`,
                            width: `${tabIndicator.width}px`,
                        }}
                        aria-hidden="true"
                    />
                </div>
            </header>

            <div className={`${styles.filtersRow} ${mobileFiltersOpen ? styles.filtersRowMobileOpen : ''} ${mobileSearchOpen ? styles.filtersRowMobileSearchOpen : ''}`}>
                <div className={styles.filtersMain}>
                    <div className={styles.filterGroup}>
                        {FILTER_CHIPS.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    className={`${styles.chip} ${chip === c.key ? styles.chipActive : ''}`}
                                    onClick={() => handleChipChange(c.key)}
                                >
                                    {c.label}
                                </button>
                            ))}
                        <div className={styles.personFilterAnchor} ref={personFilterAnchorRef}>
                            <button
                                type="button"
                                className={`${styles.chip} ${personFilterOpen || filterPersonId ? styles.chipActive : ''}`}
                                onClick={togglePersonFilterOpen}
                            >
                                {personFilterLabel}
                            </button>
                            {personFilterOpen && (
                                <div
                                    className={styles.personFilterDropdown}
                                    role="dialog"
                                    aria-label={personFilterLabel}
                                >
                                    <div className={styles.personFilterPanelHeader}>
                                        <span className={styles.personFilterPanelTitle}>
                                            {personFilterLabel}
                                        </span>
                                        <div className={styles.personFilterHeaderActions}>
                                            {filterPersonName && (
                                                <button
                                                    type="button"
                                                    className={styles.personFilterClearAll}
                                                    onClick={clearFilterPerson}
                                                >
                                                    Сбросить
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={styles.personFilterClose}
                                                onClick={() => setPersonFilterOpen(false)}
                                                aria-label="Закрыть выбор"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                    {filterPersonName && (
                                        <div className={styles.personFilterSelectedSection}>
                                            <span className={styles.personFilterSelectedLabel}>
                                                Выбрано
                                            </span>
                                            <span className={styles.selectedChip}>
                                                {filterPersonName}
                                                <button
                                                    type="button"
                                                    className={styles.chipRemove}
                                                    onClick={clearFilterPerson}
                                                    aria-label="Сбросить фильтр"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        </div>
                                    )}
                                    <div className={styles.personFilterSearchWrap}>
                                        <SearchInput
                                            className={styles.searchField}
                                            placeholder={`Поиск: ${personFilterLabel.toLowerCase()}…`}
                                            value={personFilterQuery}
                                            onChange={(e) => setPersonFilterQuery(e.target.value)}
                                            ariaLabel={`Поиск: ${personFilterLabel}`}
                                        />
                                        {personFilterResults.length > 0 && (
                                            <ul className={`${styles.pickerResults} ${styles.personFilterResults}`}>
                                                {personFilterResults.map((emp) => (
                                                    <li
                                                        key={emp.id}
                                                        className={styles.pickerItem}
                                                        onClick={() => selectFilterPerson(emp)}
                                                    >
                                                        <TaskAvatar
                                                            employeeId={emp.id}
                                                            thumb={emp.avatar_thumb}
                                                            avatar={emp.avatar}
                                                            alt={emp.name || emp.full_name || ''}
                                                            statsMap={employeeTaskStatsMap}
                                                        />
                                                        <span className={styles.pickerItemBody}>
                                                            <span className={styles.pickerItemLabel}>
                                                                {emp.name || emp.full_name || `#${emp.id}`}
                                                            </span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.searchWrap}>
                    <SearchInput
                        className={styles.searchField}
                        placeholder="Поиск задач…"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        ariaLabel="Поиск задач"
                    />
                </div>
            </div>

            <div className={styles.content}>
                {error && <div className={styles.error}>{error}</div>}
                {loading && !items.length ? (
                    <div className={styles.loading}>Загрузка…</div>
                ) : !items.length ? (
                    <div className={styles.empty}>Задач пока нет</div>
                ) : (
                    <div className={hasSidebarWidgets ? styles.layoutWithSidebar : undefined}>
                        <div>
                            {isCreatedTasksView && repeatingItems.length > 0 && (
                                <div className={styles.mobileRepeatWidget}>
                                    <div className={styles.mobileRepeatWidgetHeader}>
                                        <div className={styles.mobileRepeatWidgetTitle}>Повторяющиеся задачи</div>
                                        {repeatingItems.length > 3 && (
                                            <button
                                                type="button"
                                                className={styles.mobileRepeatWidgetToggle}
                                                onClick={() => setShowAllMobileRepeats((prev) => !prev)}
                                            >
                                                {showAllMobileRepeats ? 'Свернуть' : 'Показать все'}
                                            </button>
                                        )}
                                    </div>
                                    <div className={styles.mobileRepeatWidgetList}>
                                        {mobileRepeatingItems.map((task) => {
                                            const manageable = canManageRepeat(task);
                                            const sourceTaskId = Number(task?.repeat_rule?.source_task_id ?? task?.id);
                                            const isBusy = Number(repeatBusyId) === sourceTaskId;
                                            return (
                                                <div key={sourceTaskId} className={styles.mobileRepeatWidgetItem}>
                                                    <button
                                                        type="button"
                                                        className={styles.mobileRepeatWidgetTaskBtn}
                                                        onClick={() => openDetail(task)}
                                                    >
                                                        <span className={styles.mobileRepeatWidgetTaskTitle}>{task.title}</span>
                                                        <span className={styles.mobileRepeatWidgetTaskMeta}>
                                                            {getRepeatFrequencyLabel(task?.repeat_rule?.frequency)}
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.mobileRepeatWidgetDisableBtn}
                                                        onClick={() => disableTaskRepeat(task)}
                                                        disabled={!manageable || isBusy}
                                                        title={manageable ? 'Отключить повтор' : 'Отключить может только автор задачи'}
                                                    >
                                                        {isBusy ? 'Отключение…' : 'Отключить'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <ul className={styles.cardList}>{sortedItems.map(renderCard)}</ul>

                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            {renderTableSortHeader('title', 'Задача')}
                                            {renderTableSortHeader('deadline', 'Срок')}
                                            {renderTableSortHeader(
                                                'person',
                                                isCreatedView ? 'Исполнитель' : 'Инициатор'
                                            )}
                                            {renderTableSortHeader('status', 'Статус')}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedItems.map((task) => {
                                            const person = isCreatedView ? task.assignee : task.initiator;
                                            const unreadCommentsCount = getUnreadCommentsCount(task);
                                            return (
                                                <tr key={task.id} onClick={() => openDetail(task)}>
                                                    <td className={styles.tableTitle}>
                                                        {task.title}
                                                        {unreadCommentsCount > 0 && (
                                                            <span className={styles.cardCommentBadge} aria-label={`Новых комментариев: ${unreadCommentsCount}`}>
                                                                {unreadCommentsCount > 99 ? '99+' : unreadCommentsCount}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className={isDeadlineUrgent(task) ? styles.cardDeadlineOverdue : ''} style={{ whiteSpace: 'nowrap' }}>
                                                        {formatDeadline(task.deadline_at)}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{person?.name || '—'}</td>
                                                    <td><StatusBadge status={task.status} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {hasMore && (
                                <button type="button" className={styles.loadMoreBtn} onClick={() => loadList(false, offset)}>
                                    Загрузить ещё
                                </button>
                            )}
                        </div>

                        {isMyTasksView && (
                            <aside className={styles.sidebarWidgets}>
                                <div className={styles.widget}>
                                    <div className={styles.widgetTitle}>Мои задачи</div>
                                    <div className={styles.widgetStatsRow}>
                                        <div className={styles.widgetStatCol}>
                                            <div className={styles.widgetBig}>{stats.total}</div>
                                            <div className={styles.widgetStatLabel}>всего</div>
                                        </div>
                                        <div className={styles.widgetStatCol}>
                                            <div className={`${styles.widgetBig} ${styles.widgetPct}`}>
                                                {stats.pct}%
                                            </div>
                                            <div className={styles.widgetStatLabel}>выполнено</div>
                                        </div>
                                    </div>
                                    <div className={styles.widgetBreakdown}>
                                        <div className={styles.widgetBreakdownRow}>
                                            <span className={styles.widgetIconDone} aria-hidden="true">
                                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                                    <path
                                                        d="M1 4L3.5 6.5L9 1"
                                                        stroke="white"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                            <span className={styles.widgetBreakdownNum}>{stats.done}</span>
                                            <span className={styles.widgetBreakdownText}>выполнено</span>
                                        </div>
                                        <div className={styles.widgetBreakdownRow}>
                                            <span className={styles.widgetIconRemaining} aria-hidden="true" />
                                            <span className={styles.widgetBreakdownNum}>{stats.remaining}</span>
                                            <span className={styles.widgetBreakdownText}>осталось</span>
                                        </div>
                                    </div>
                                </div>
                                {stats.overdue > 0 && (
                                    <div className={styles.widget}>
                                        <div className={styles.widgetTitle}>Просроченные</div>
                                        <div className={styles.widgetOverdueCount}>
                                            <span className={styles.widgetOverdueNum}>{stats.overdue}</span>
                                            <span className={styles.widgetOverdueLabel}>
                                                {pluralizeTasks(stats.overdue)}
                                            </span>
                                        </div>
                                        <div className={styles.widgetOverdueList}>
                                            {overdueItems.map((t) => (
                                                <button
                                                    key={t.id}
                                                    type="button"
                                                    className={styles.widgetOverdueItem}
                                                    onClick={() => openDetail(t)}
                                                >
                                                    <div className={styles.widgetOverdueItemTitle}>{t.title}</div>
                                                    <div className={styles.widgetOverdueItemDate}>
                                                        {formatDeadline(t.deadline_at)}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.widgetShowAllBtn}
                                            onClick={() => handleChipChange('overdue')}
                                        >
                                            Показать все
                                        </button>
                                    </div>
                                )}
                            </aside>
                        )}
                        {isCreatedTasksView && (
                            <aside className={styles.sidebarWidgets}>
                                {repeatingItems.length > 0 && (
                                    <div className={styles.widget}>
                                        <div className={styles.widgetTitle}>Повторяющиеся задачи</div>
                                        <div className={styles.repeatWidgetList}>
                                            {repeatingItems.slice(0, 6).map((task) => {
                                                const manageable = canManageRepeat(task);
                                                const sourceTaskId = Number(task?.repeat_rule?.source_task_id ?? task?.id);
                                                const isBusy = Number(repeatBusyId) === sourceTaskId;
                                                return (
                                                    <div key={sourceTaskId} className={styles.repeatWidgetItem}>
                                                        <button
                                                            type="button"
                                                            className={styles.repeatWidgetTaskBtn}
                                                            onClick={() => openDetail(task)}
                                                        >
                                                            <span className={styles.repeatWidgetTaskTitle}>{task.title}</span>
                                                            <span className={styles.repeatWidgetTaskMeta}>
                                                                {getRepeatFrequencyLabel(task?.repeat_rule?.frequency)}
                                                            </span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={styles.repeatWidgetDisableBtn}
                                                            onClick={() => disableTaskRepeat(task)}
                                                            disabled={!manageable || isBusy}
                                                            title={manageable ? 'Отключить повтор' : 'Отключить может только автор задачи'}
                                                        >
                                                            {isBusy ? 'Отключение…' : 'Отключить'}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </aside>
                        )}
                    </div>
                )}
            </div>

            <button
                type="button"
                className={styles.mobileFab}
                aria-label="Новая задача"
                onClick={openCreateModal}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 1V15M1 8H15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>

            {detailId && (
                <div className={styles.overlay} onClick={closeDetail} role="presentation">
                    <div
                        ref={detailModalRef}
                        className={`${styles.modal} ${styles.modalWide}`}
                        onClick={(e) => e.stopPropagation()}
                        onDragEnter={handleDetailDragEnter}
                        onDragOver={handleDetailDragOver}
                        onDragLeave={handleDetailDragLeave}
                        onDrop={handleDetailDrop}
                        role="dialog"
                        aria-modal="true"
                    >
                        {detailDragActive && (
                            <div className={styles.modalDragOverlay} aria-hidden="true">
                                <span className={styles.modalDragOverlayText}>
                                    Прикрепить к комментарию
                                </span>
                            </div>
                        )}
                        <div className={styles.detailMobileShell}>
                            <div
                                ref={detailMobileScrollRef}
                                className={styles.detailMobileScroll}
                            >
                        <div className={styles.modalHeader}>
                            <div className={styles.modalHeaderMain}>
                                {detail && (
                                    <>
                                        <div className={styles.mobileDetailTopBar}>
                                            <button
                                                type="button"
                                                className={styles.mobileDetailBack}
                                                onClick={closeDetail}
                                                aria-label="Назад"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <path d="M15 8H1M8 1L1 8L8 15" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                            <span className={styles.mobileDetailTopTitle}>Карточка задачи</span>
                                            <div className={styles.mobileDetailTopActions}>
                                                {canManageTask && isMobileDetailView && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={styles.mobileDetailIconBtn}
                                                            onClick={openEdit}
                                                            aria-label="Редактировать задачу"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <path d="M13.3333 13.5523H8M1 13.8856L4.69951 12.4627C4.93614 12.3717 5.05445 12.3262 5.16515 12.2668C5.26347 12.214 5.3572 12.1531 5.44536 12.0847C5.54461 12.0077 5.63425 11.918 5.81352 11.7387L13.3333 4.21896C14.0697 3.48258 14.0697 2.28866 13.3333 1.55228C12.597 0.815909 11.4031 0.815903 10.6667 1.55228L3.14685 9.07208C2.96759 9.25134 2.87795 9.34101 2.80093 9.44028C2.73251 9.52841 2.6716 9.62214 2.61882 9.72048C2.55941 9.83114 2.5139 9.94948 2.42289 10.1861L1 13.8856ZM1 13.8856L2.37208 10.3183C2.47027 10.063 2.51935 9.93534 2.60356 9.87688C2.67715 9.82581 2.7682 9.80648 2.8562 9.82328C2.95689 9.84248 3.05359 9.93921 3.24699 10.1326L4.75305 11.6387C4.94645 11.8321 5.04315 11.9287 5.06237 12.0294C5.07918 12.1174 5.05986 12.2085 5.00876 12.2821C4.9503 12.3663 4.82266 12.4153 4.56738 12.5135L1 13.8856Z" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </button>
                                                        <ActionsMenu
                                                            open={detailMenuOpen}
                                                            onToggle={(e) => {
                                                                e.stopPropagation();
                                                                setDetailMenuOpen((v) => !v);
                                                            }}
                                                            title="Удаление задачи"
                                                            align="left"
                                                            trigger={<ActionsMenuDotsIcon />}
                                                            wrapClassName={styles.detailMenuWrap}
                                                            buttonClassName={styles.mobileDetailIconBtn}
                                                            portal
                                                            portalZIndex={13100}
                                                            items={[
                                                                {
                                                                    key: 'delete',
                                                                    label: 'Удалить',
                                                                    danger: true,
                                                                    onClick: () => deleteTask(false),
                                                                },
                                                                ...(detail.group_id ? [{
                                                                    key: 'delete-all',
                                                                    label: 'Удалить у всех',
                                                                    danger: true,
                                                                    onClick: () => deleteTask(true),
                                                                }] : []),
                                                            ]}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.detailHeaderTop}>
                                            <div className={styles.detailTags}>
                                                <StatusBadge status={detail.status} />
                                                {detail.is_important && <span className={styles.importantTag}>Важное</span>}
                                            </div>
                                            <div className={styles.detailHeaderActions}>
                                                {canManageTask && !isMobileDetailView && (
                                                    <>
                                                        <ActionsMenu
                                                            open={detailMenuOpen}
                                                            onToggle={(e) => {
                                                                e.stopPropagation();
                                                                setDetailMenuOpen((v) => !v);
                                                            }}
                                                            title="Действия с задачей"
                                                            align="left"
                                                            trigger={<ActionsMenuDotsIcon />}
                                                            wrapClassName={styles.detailMenuWrap}
                                                            buttonClassName={styles.detailMenuBtn}
                                                            portal
                                                            portalZIndex={13100}
                                                            items={[
                                                                {
                                                                    key: 'edit',
                                                                    label: 'Редактировать',
                                                                    onClick: openEdit,
                                                                },
                                                                {
                                                                    key: 'delete',
                                                                    label: 'Удалить',
                                                                    danger: true,
                                                                    onClick: () => deleteTask(false),
                                                                },
                                                                ...(detail.group_id ? [{
                                                                    key: 'delete-all',
                                                                    label: 'Удалить у всех',
                                                                    danger: true,
                                                                    onClick: () => deleteTask(true),
                                                                }] : []),
                                                            ]}
                                                        />
                                                    </>
                                                )}
                                                <button type="button" className={styles.modalClose} onClick={closeDetail} aria-label="Закрыть">
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M11 1L1 11M1 1L11 11" stroke="#717680" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <h3
                                                className={`${styles.detailTitle} ${hasMediaAttachments ? styles.detailTitleMediaLink : ''}`}
                                                onClick={hasMediaAttachments ? () => setMediaPanelOpen(true) : undefined}
                                                onKeyDown={hasMediaAttachments ? (e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        setMediaPanelOpen(true);
                                                    }
                                                } : undefined}
                                                role={hasMediaAttachments ? 'button' : undefined}
                                                tabIndex={hasMediaAttachments ? 0 : undefined}
                                                title={hasMediaAttachments ? 'Открыть медиатеку задачи' : undefined}
                                            >
                                                {detail.title}
                                            </h3>
                                            <div className={styles.detailMetaBar}>
                                                <div className={styles.detailMeta}>
                                                    <div className={`${styles.detailMetaRow} ${styles.detailMetaRowInitiator}`}>
                                                        <span className={styles.detailMetaLabel}>{detailCounterpartyLabel}: </span>
                                                        <span className={styles.detailMetaValue}>
                                                            {detailCounterparty?.name || '—'}
                                                        </span>
                                                    </div>
                                                    <div className={styles.detailMetaDates}>
                                                        <div className={styles.detailMetaRow}>
                                                            <span className={styles.detailMetaLabel}>Создано: </span>
                                                            <span className={styles.detailMetaValue}>
                                                                {formatDateTime(detail.created_at)}
                                                            </span>
                                                        </div>
                                                        <div className={`${styles.detailMetaRow} ${styles.detailMetaRowDeadline}`}>
                                                            <span className={styles.detailMetaLabel}>Дедлайн: </span>
                                                            <span
                                                                className={`${styles.detailMetaValue} ${isDeadlineUrgent(detail) ? styles.detailMetaValueOverdue : ''}`}
                                                            >
                                                                {formatDeadline(detail.deadline_at)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {canChangeTaskStatus && (
                                                    <div className={styles.detailStatusRow}>
                                                        <Select
                                                            className={`${styles.statusSelect} ${statusSelectTone}`}
                                                            value={detail.status}
                                                            onChange={(e) => {
                                                                const next = e.target.value;
                                                                if (next && next !== detail.status) changeStatus(next);
                                                            }}
                                                            disabled={statusBusy}
                                                            ariaLabel="Статус задачи"
                                                        >
                                                            {statusSelectOptions.map((st) => {
                                                                const meta = STATUS_META[st];
                                                                return (
                                                                    <option key={st} value={st}>
                                                                        {meta?.label || st}
                                                                    </option>
                                                                );
                                                            })}
                                                        </Select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className={`${styles.modalBody} ${styles.modalBodyDetail}`}>
                            {!detail ? (
                                detailLoading ? (
                                    <div className={styles.loading}>Загрузка…</div>
                                ) : null
                            ) : (
                                <>
                                    <div className={styles.detailScroll} ref={detailScrollRef}>
                                        {canChangeTaskStatus && (
                                            <div className={styles.mobileStatusPanel}>
                                                <div className={styles.mobileStatusLabel}>Изменить статус</div>
                                                <div className={styles.mobileStatusChips}>
                                                    {statusSelectOptions.map((st) => {
                                                        const meta = STATUS_META[st];
                                                        return (
                                                            <button
                                                                key={st}
                                                                type="button"
                                                                className={`${styles.mobileStatusChip} ${detail.status === st ? styles.mobileStatusChipActive : ''}`}
                                                                onClick={() => {
                                                                    if (st !== detail.status) changeStatus(st);
                                                                }}
                                                                disabled={statusBusy || st === detail.status}
                                                            >
                                                                {meta?.label || st}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {detail.description && (
                                            <>
                                                <div className={styles.sectionTitle}>Описание</div>
                                                <p className={styles.description}>{detail.description}</p>
                                            </>
                                        )}
                                        {subtasksTotal > 0 && (
                                            <>
                                                <div className={styles.sectionTitle}>
                                                    Подзадачи · {subtasksDone}/{subtasksTotal}
                                                </div>
                                                <div className={styles.progressBar}>
                                                    <div className={styles.progressFill} style={{ width: `${subPct}%` }} />
                                                </div>
                                                <ul className={styles.subtaskList}>
                                                    {detail.subtasks.map((st) => (
                                                        <li key={st.id} className={styles.subtaskItem}>
                                                            <input
                                                                type="checkbox"
                                                                className={styles.subtaskCheck}
                                                                checked={!!st.is_done}
                                                                onChange={() => toggleSubtask(st)}
                                                            />
                                                            <span className={st.is_done ? styles.subtaskDone : ''}>{st.title}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        {detail.attachments?.length > 0 && (
                                            <>
                                                <div className={styles.sectionTitle}>Вложения</div>
                                                <div className={styles.attachList}>
                                                    <AttachmentList
                                                        attachments={detail.attachments}
                                                        taskId={detail.id}
                                                        onImageOpen={openImageLightbox}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className={styles.sectionTitle}>Комментарии</div>
                                        <div className={styles.commentList}>
                                            {(detail.comments || []).map((c) => {
                                                const isOwnComment = Number(c.author_employee_id) === Number(employeeId);
                                                const isEditingComment = Number(editingCommentId) === Number(c.id);
                                                const isCommentDeleting = Number(deletingCommentId) === Number(c.id);
                                                const isCommentMenuOpen = Number(commentMenuOpenId) === Number(c.id);
                                                const hasBubble = Boolean(
                                                    c.text?.trim()
                                                    || (c.attachments && c.attachments.length > 0)
                                                );
                                                return (
                                                    <div
                                                        key={c.id}
                                                        id={`task-comment-${c.id}`}
                                                        className={styles.comment}
                                                    >
                                                        <TaskAvatar
                                                            employeeId={c.author?.id ?? c.author_id}
                                                            thumb={c.author?.avatar_thumb}
                                                            avatar={c.author?.avatar}
                                                            alt={c.author?.name || ''}
                                                            statsMap={employeeTaskStatsMap}
                                                        />
                                                        <div className={styles.commentContent}>
                                                            {hasBubble && (
                                                                <div
                                                                    className={`${styles.commentBubble} ${isEditingComment ? styles.commentBubbleEditing : ''}`}
                                                                >
                                                                    {isOwnComment && !isEditingComment && (
                                                                        <div className={styles.commentMenuWrap}>
                                                                            <button
                                                                                type="button"
                                                                                className={styles.commentMenuBtn}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    commentMenuAnchorRef.current = e.currentTarget;
                                                                                    setCommentMenuOpenId((openId) => (
                                                                                        Number(openId) === Number(c.id) ? null : c.id
                                                                                    ));
                                                                                }}
                                                                                aria-haspopup="menu"
                                                                                aria-expanded={isCommentMenuOpen}
                                                                                aria-label="Действия с комментарием"
                                                                                disabled={isCommentDeleting}
                                                                            >
                                                                                <ActionsMenuDotsIcon />
                                                                            </button>
                                                                            <DropdownMenu
                                                                                open={isCommentMenuOpen}
                                                                                align="left"
                                                                                portal
                                                                                anchorRef={commentMenuAnchorRef}
                                                                                items={[
                                                                                    {
                                                                                        key: 'edit',
                                                                                        label: 'Редактировать',
                                                                                        onClick: () => startEditComment(c),
                                                                                    },
                                                                                    {
                                                                                        key: 'delete',
                                                                                        label: isCommentDeleting ? 'Удаление…' : 'Удалить',
                                                                                        danger: true,
                                                                                        disabled: isCommentDeleting,
                                                                                        onClick: () => deleteComment(c),
                                                                                    },
                                                                                ]}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {c.text?.trim() && (
                                                                        <div className={styles.commentText}>{c.text}</div>
                                                                    )}
                                                                    {c.attachments?.length > 0 && (
                                                                        <AttachmentList
                                                                            attachments={c.attachments}
                                                                            taskId={detail.id}
                                                                            onImageOpen={openImageLightbox}
                                                                            imagePreviewClassName={styles.commentAttachPreview}
                                                                        />
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className={styles.commentMeta}>
                                                                <span>{c.author?.name || '—'}</span>
                                                                <span className={styles.commentMetaDot}> · </span>
                                                                <span>{formatCommentTime(c.created_at)}</span>
                                                                {c.updated_at && c.updated_at !== c.created_at && (
                                                                    <>
                                                                        <span className={styles.commentMetaDot}> · </span>
                                                                        <span>изменён</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {(detail.comments || []).length === 0 && (
                                                <div className={styles.empty}>Пока нет комментариев</div>
                                            )}
                                            {commentsLoading && commentsHasMore && (
                                                <div className={styles.commentsPagingHint}>Загрузка комментариев…</div>
                                            )}
                                        </div>
                                    </div>

                                    {renderDetailComposer(styles.detailFooterDesktopDock)}

                                </>
                            )}
                        </div>
                            </div>
                            {detail && renderDetailComposer(styles.detailFooterMobileDock, detailComposerRef)}
                        </div>
                        {detail && (
                            <TaskMediaPanel
                                isOpen={mediaPanelOpen}
                                onClose={() => setMediaPanelOpen(false)}
                                task={detail}
                                onOpenImageLightbox={openImageLightbox}
                            />
                        )}
                    </div>
                </div>
            )}

            {editOpen && detail && (
                <div className={`${styles.overlay} ${styles.overlayAbove}`} onClick={() => setEditOpen(false)} role="presentation">
                    <div
                        ref={editModalRef}
                        className={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Редактировать задачу</h2>
                            <button type="button" className={styles.modalClose} onClick={() => setEditOpen(false)} aria-label="Закрыть">
                                ✕
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Название</label>
                                <Input
                                    className={styles.formField}
                                    value={editTitle}
                                    onChange={(e) => handleTaskTitleChange(e.target.value, setEditTitle, setEditTitleError)}
                                    ariaLabel="Название задачи"
                                />
                                {editTitleError && (
                                    <div className={styles.formFieldError} role="alert">{editTitleError}</div>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Описание</label>
                                <Textarea
                                    wrapperClassName={styles.formFieldArea}
                                    value={editDesc}
                                    onChange={(e) => setEditDesc(e.target.value)}
                                    ariaLabel="Описание задачи"
                                    rows={4}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Дедлайн</label>
                                <div className={styles.formRow2}>
                                    <div className={styles.deadlineCol}>
                                        <span className={styles.mobileFieldHint}>Дата</span>
                                        <Input
                                            type="date"
                                            className={styles.formField}
                                            value={editDeadlineDate}
                                            onChange={(e) => setEditDeadlineDate(e.target.value)}
                                            ariaLabel="Дата дедлайна"
                                            hideClear
                                        />
                                    </div>
                                    <div className={styles.deadlineCol}>
                                        <span className={styles.mobileFieldHint}>Время</span>
                                        <Input
                                            type="time"
                                            className={styles.formField}
                                            value={editDeadlineTime}
                                            onChange={(e) => setEditDeadlineTime(e.target.value)}
                                            ariaLabel="Время дедлайна"
                                            hideClear
                                            disabled={!editDeadlineDate}
                                        />
                                    </div>
                                </div>
                            </div>
                            <label className={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    checked={editImportant}
                                    onChange={(e) => setEditImportant(e.target.checked)}
                                />
                                Важная задача
                            </label>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Подзадачи</label>
                                {editSubtasks.map((st, idx) => {
                                    const canRemoveSubtask =
                                        editSubtasks.length > 1 || Boolean(st.title.trim());
                                    return (
                                        <div key={st.id ?? `new-${idx}`} className={styles.subtaskPill}>
                                            <Input
                                                className={styles.subtaskPillInput}
                                                value={st.title}
                                                placeholder="Подзадача"
                                                onChange={(e) => {
                                                    const next = [...editSubtasks];
                                                    next[idx] = { ...next[idx], title: e.target.value };
                                                    setEditSubtasks(next);
                                                }}
                                                ariaLabel="Подзадача"
                                                hideClear
                                            />
                                            {canRemoveSubtask && (
                                                <button
                                                    type="button"
                                                    className={styles.subtaskPillRemove}
                                                    aria-label="Удалить подзадачу"
                                                    onClick={() => {
                                                        setEditSubtasks((prev) => {
                                                            const next = prev.filter((_, i) => i !== idx);
                                                            return next.length
                                                                ? next
                                                                : [{ id: null, title: '', is_done: false }];
                                                        });
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                                <button
                                    type="button"
                                    className={styles.chip}
                                    onClick={() => setEditSubtasks((prev) => [
                                        ...prev,
                                        { id: null, title: '', is_done: false },
                                    ])}
                                >
                                    + Подзадача
                                </button>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.submitBtn}
                                disabled={editBusy || !editTitle.trim()}
                                onClick={saveEdit}
                            >
                                {editBusy ? 'Сохранение…' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {createOpen && (
                <div className={styles.overlay} role="presentation">
                    <div
                        ref={createModalRef}
                        className={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                        onPaste={handleCreatePaste}
                        onDragEnter={handleCreateModalDragEnter}
                        onDragOver={handleCreateModalDragOver}
                        onDragLeave={handleCreateModalDragLeave}
                        onDrop={handleCreateModalDrop}
                        role="dialog"
                        aria-modal="true"
                    >
                        {createDragActive && (
                            <div className={styles.modalDragOverlay} aria-hidden="true">
                                <span className={styles.modalDragOverlayText}>Отпустите файлы для прикрепления</span>
                            </div>
                        )}
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Новая задача</h2>
                            <button type="button" className={styles.modalClose} onClick={closeCreateModal} aria-label="Закрыть">
                                ✕
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Название <span style={{ color: '#C01048', fontWeight: '400' }}>*</span></label>
                                <Input
                                    className={styles.formField}
                                    value={createTitle}
                                    onChange={(e) => handleTaskTitleChange(e.target.value, setCreateTitle, setCreateTitleError)}
                                    placeholder="Введите название задачи"
                                    ariaLabel="Название задачи"
                                />
                                {createTitleError && (
                                    <div className={styles.formFieldError} role="alert">{createTitleError}</div>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Описание</label>
                                <Textarea
                                    wrapperClassName={styles.formFieldArea}
                                    value={createDesc}
                                    onChange={(e) => setCreateDesc(e.target.value)}
                                    placeholder="Подробное описание..."
                                    ariaLabel="Описание задачи"
                                    rows={4}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <div className={styles.formLabelRow}>
                                    <label className={styles.formLabel}>
                                        Исполнители <span style={{ color: '#C01048', fontWeight: '400' }}>*</span>
                                    </label>
                                    <label className={styles.assignSelfCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={isSelfAssignee}
                                            onChange={toggleAssignToSelf}
                                            disabled={!currentEmployeeAssignee}
                                        />
                                        Себе
                                    </label>
                                </div>
                                <Input
                                    className={styles.formField}
                                    placeholder="Сотрудник, отдел, команда, сторона…"
                                    value={assigneeQuery}
                                    onChange={(e) => setAssigneeQuery(e.target.value)}
                                    ariaLabel="Поиск исполнителей"
                                />
                                {assigneeSuggestions.length > 0 && (
                                    <ul className={styles.pickerResults}>
                                        {assigneeSuggestions.map((item) => (
                                            <li
                                                key={`${item.kind}-${item.id}`}
                                                className={styles.pickerItem}
                                                onClick={() => pickAssigneeSuggestion(item)}
                                            >
                                                {item.kind === 'employee' && (
                                                    <TaskAvatar
                                                        employeeId={item.data?.id}
                                                        thumb={item.data?.avatar_thumb}
                                                        avatar={item.data?.avatar}
                                                        alt={item.label || ''}
                                                        statsMap={employeeTaskStatsMap}
                                                    />
                                                )}
                                                <span className={styles.pickerItemBody}>
                                                    <span className={styles.pickerItemLabel}>{item.label}</span>
                                                    <span className={styles.pickerItemKind}>
                                                        {ASSIGNEE_PICKER_LABELS[item.kind]}
                                                    </span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {hasAssigneeSelection && (
                                    <div className={styles.chipsSelected}>
                                        {selectedDeptIds.map((id) => {
                                            const d = departments.find((x) => x.id === id);
                                            return (
                                                <span key={`dept-${id}`} className={`${styles.selectedChip} ${styles.selectedChipDept}`}>
                                                    {d?.name || d?.title || `Отдел #${id}`}
                                                    <button
                                                        type="button"
                                                        className={styles.chipRemove}
                                                        onClick={() => setSelectedDeptIds((prev) => prev.filter((x) => x !== id))}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                        {selectedTeamIds.map((id) => {
                                            const t = teams.find((x) => x.id === id);
                                            return (
                                                <span key={`team-${id}`} className={`${styles.selectedChip} ${styles.selectedChipTeam}`}>
                                                    {t?.name || `Команда #${id}`}
                                                    <button
                                                        type="button"
                                                        className={styles.chipRemove}
                                                        onClick={() => setSelectedTeamIds((prev) => prev.filter((x) => x !== id))}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                        {selectedSides.map((side) => {
                                            const label = ASSIGNEE_SIDE_OPTIONS.find((s) => s.id === side)?.label
                                                || (side === 'light' ? 'Светлая' : 'Тёмная');
                                            return (
                                                <span key={`side-${side}`} className={`${styles.selectedChip} ${styles.selectedChipSide}`}>
                                                    {label}
                                                    <button
                                                        type="button"
                                                        className={styles.chipRemove}
                                                        onClick={() => setSelectedSides((prev) => prev.filter((s) => s !== side))}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                        {selectedAssignees.map((emp) => (
                                            <span key={`emp-${emp.id}`} className={styles.selectedChip}>
                                                {emp.name || emp.full_name}
                                                <button
                                                    type="button"
                                                    className={styles.chipRemove}
                                                    onClick={() => setSelectedAssignees((prev) => prev.filter((x) => x.id !== emp.id))}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Дедлайн</label>
                                <div className={styles.formRow2}>
                                    <div className={styles.deadlineCol}>
                                        <span className={styles.mobileFieldHint}>Дата</span>
                                        <Input
                                            type="date"
                                            className={styles.formField}
                                            value={createDeadlineDate}
                                            onChange={(e) => setCreateDeadlineDate(e.target.value)}
                                            ariaLabel="Дата дедлайна"
                                            hideClear
                                        />
                                    </div>
                                    <div className={styles.deadlineCol}>
                                        <span className={styles.mobileFieldHint}>Время</span>
                                        <Input
                                            type="time"
                                            className={styles.formField}
                                            value={createDeadlineTime}
                                            onChange={(e) => setCreateDeadlineTime(e.target.value)}
                                            ariaLabel="Время дедлайна"
                                            hideClear
                                            disabled={!createDeadlineDate}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Повторение</label>
                                <Select
                                    className={styles.formField}
                                    value={createRepeat}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setCreateRepeat(next);
                                        if (!next) {
                                            setCreateRepeatDate('');
                                            setCreateRepeatTime('');
                                            return;
                                        }
                                        if (!createRepeatDate && createDeadlineDate) {
                                            setCreateRepeatDate(createDeadlineDate);
                                        }
                                        if (!createRepeatTime && createDeadlineTime) {
                                            setCreateRepeatTime(createDeadlineTime);
                                        }
                                    }}
                                    ariaLabel="Повторение задачи"
                                >
                                    {REPEAT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Select>
                                {createRepeat && (
                                    <div className={styles.repeatNextRun}>
                                        <label className={styles.formSubLabel}>
                                            Первое автосоздание следующей задачи <span style={{ color: '#C01048' }}>*</span>
                                        </label>
                                        <div className={styles.formRow2}>
                                            <div className={styles.deadlineCol}>
                                                <span className={styles.mobileFieldHint}>Дата</span>
                                                <Input
                                                    type="date"
                                                    className={styles.formField}
                                                    value={createRepeatDate}
                                                    onChange={(e) => setCreateRepeatDate(e.target.value)}
                                                    ariaLabel="Дата первого автосоздания"
                                                    hideClear
                                                />
                                            </div>
                                            <div className={styles.deadlineCol}>
                                                <span className={styles.mobileFieldHint}>Время</span>
                                                <Input
                                                    type="time"
                                                    className={styles.formField}
                                                    value={createRepeatTime}
                                                    onChange={(e) => setCreateRepeatTime(e.target.value)}
                                                    ariaLabel="Время первого автосоздания"
                                                    hideClear
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <label className={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    checked={createImportant}
                                    onChange={(e) => setCreateImportant(e.target.checked)}
                                />
                                Важная задача
                            </label>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Подзадачи</label>
                                {createSubtasks.map((st, idx) => {
                                    const canRemoveSubtask =
                                        createSubtasks.length > 1 || Boolean(st.trim());
                                    return (
                                        <div key={idx} className={styles.subtaskPill}>
                                            <Input
                                                className={styles.subtaskPillInput}
                                                value={st}
                                                placeholder="Подзадача"
                                                onChange={(e) => {
                                                    const next = [...createSubtasks];
                                                    next[idx] = e.target.value;
                                                    setCreateSubtasks(next);
                                                }}
                                                ariaLabel="Подзадача"
                                                hideClear
                                            />
                                            {canRemoveSubtask && (
                                                <button
                                                    type="button"
                                                    className={styles.subtaskPillRemove}
                                                    aria-label="Удалить подзадачу"
                                                    onClick={() => {
                                                        setCreateSubtasks((prev) => {
                                                            const next = prev.filter((_, i) => i !== idx);
                                                            return next.length ? next : [''];
                                                        });
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                                <button
                                    type="button"
                                    className={styles.chip}
                                    onClick={() => setCreateSubtasks((prev) => [...prev, ''])}
                                >
                                    + Подзадача
                                </button>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Вложения</label>
                                <label
                                    className={`${styles.fileDrop} ${createDragActive ? styles.fileDropActive : ''}`}
                                >
                                    <span className={styles.fileDropTitle}>Перетащите файлы на окно или сюда</span>
                                    <span className={styles.fileDropHint}>Нажмите для выбора файлов</span>
                                    <input
                                        ref={createFileInputRef}
                                        type="file"
                                        multiple
                                        onChange={(e) => {
                                            mergeCreateFiles(e.target.files);
                                            e.target.value = '';
                                        }}
                                    />
                                </label>
                                {createFiles.length > 0 && (
                                    <ul className={styles.fileList}>
                                        {createFiles.map((f, idx) => (
                                            <li
                                                key={`${f.name}-${f.size}-${f.lastModified}`}
                                                className={styles.fileListItem}
                                            >
                                                <span className={styles.fileListName}>{f.name}</span>
                                                <button
                                                    type="button"
                                                    className={styles.fileListRemove}
                                                    aria-label={`Удалить ${f.name}`}
                                                    onClick={() => removeCreateFile(idx)}
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.submitBtn}
                                disabled={createBusy || !canCreateTask}
                                onClick={handleCreate}
                            >
                                {createBusy ? 'Создание…' : 'Создать задачу'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {imageLightbox && (
                <ImageLightbox
                    images={imageLightbox.images}
                    index={imageLightbox.index}
                    onClose={() => setImageLightbox(null)}
                    onIndexChange={(index) => setImageLightbox((prev) => (
                        prev ? { ...prev, index } : null
                    ))}
                />
            )}

            <TaskNotificationsPanel
                isOpen={notifPanelOpen}
                onClose={() => setNotifPanelOpen(false)}
                items={filterTaskNotificationsForFeed(notifications)}
                loading={notifLoading}
                hasMore={notifHasMore}
                onLoadMore={() => {
                    if (!notifLoading && notifHasMore) loadNotifications(false);
                }}
                onOpenItem={openNotification}
                onMarkAllRead={handleMarkAllNotificationsRead}
                markAllBusy={markAllNotifBusy}
            />
        </div>
    );
}
